/**
 * interior_planner.ts — THE BOUNDED PLANNER (the interiors programme, slice B, 2026-10-01; report §4.3). PURE and
 * DETERMINISTIC: the same input gives the same descriptor and the same digest on every machine.
 *
 *   planInterior(input) → { descriptor, report }
 *
 *   1. RESERVE  — the door corridor and the stair zone (the LEGACY functions `slotClear` / `footprintClear`, consumed
 *                 with the floor's frame — never copied), the shell's own collide boxes (`interiorSpec`: sheets, rails,
 *                 ramps, columns, plinths — and the slab OPENING on an upper floor), the structure console's box and
 *                 standing room on a deed's ground floor (`consoleSeat`, `offTheConsole`'s keep-out), the LIFT
 *                 candidate's shaft and apron (≥ 3 floors), the landing apron, and every player-OWNED piece with its
 *                 use envelope, as locked content.
 *   2. PROGRAMS — the archetype's arrangements for the floor's size band and tradition, in their stable order: the
 *                 first K.
 *   3. ZONES    — optional zones are dropped (highest `dropOrder` first) until the mandatory areas fit the usable
 *                 area; a mandatory program that cannot fit rejects the candidate.
 *   4. KITS     — each zone's fills seat kits at their sites (wall run / free row / island grid / spot): the first
 *                 (kit, site, slide step, mirror) whose every piece passes the hard piece rules.
 *   5. ROUTE    — a conservative 0.1 m raster: a cell is free only when the WHOLE cell is ≥ capsule radius + 5 cm from
 *                 every solid, so 4-connected free cells are a continuous path for the REAL capsule (r 0.3 m). A flood
 *                 from the floor's entry must reach the stair foot, the console, the lift call and every anchor.
 *   6. REPAIR   — ≤ R rounds: seats nobody can reach are removed; the lowest-value optional zone is dropped; re-prove.
 *   7. RANK     — valid candidates by an integer soft score (zones kept, adjacency, short routes, a queue the entry
 *                 can see, windows, public before private, seats that face something, a little asymmetry) + a seeded
 *                 jitter; ties by candidate order. THE SCORE READS THE PRIMARY PASS ONLY.
 *   8. DRESS    — the winner takes its secondary fills, then its lived-in detail, each from its OWN seed stream, each
 *                 piece accepted only if it touches no cell of a proven route and no reserved envelope — so a vase
 *                 cannot move the bed, and every pack PREFIX of the emitted order is a valid room.
 *   9. LADDER   — full → simpler (the archetype's fallback) → sparse (mandatory zones, bare kits) → the LEGACY plan
 *                 (`purposePlan`, the baseline retained) → empty with a reason. Upper floors fall back on their own.
 *
 * `algorithm: 'legacy'` returns today's `purposePlan` slots UNTOUCHED (slot ids `legacy/<floor>/<index>`); so does every
 * archetype that is `legacy.<purpose>`.
 *
 * WORK BUDGETS, never wall-clock: K candidates, R repairs, a fixed slide per site, a fixed raster cell.
 * UNITS: integer millimetres inside (positions on a 10 mm grid); quarter turns only for generated pieces.
 *
 * THE STAIR ZONE (`options.stairZone`): `shell` (the default) keeps law 22's stair zone wherever the shell BUILDS a run
 * or an opening — every multi-floor building, every floor — and frees it in a ONE-FLOOR shell, where `interiorSpec`
 * builds no stairs at all; `law` reserves it in every shell exactly as today's plans do. See the hand-back.
 *
 * NOT a service model: an anchor's `access` is a policy request. Nothing here grants, charges or stores anything.
 */
import { interiorSpec, FLOOR_H, LIFT_WAY_OUT_M, type InteriorSpec, type LiftShaftRule } from './building_interiors.js';
import { FURNITURE } from './furniture_catalog.js';
import { purposePlan, slotClear, footprintClear, drawnHalf, stairEdgeX, DOOR_BOX_HALF, type PlanFrame, type PlanSlot } from './interior_plans.js';
import { consoleSeat } from './structure_access.js';
import { CONSOLE_KEEP_W, CONSOLE_KEEP_FRONT, CONSOLE_KEEP_BACK, offTheConsole, packFloors, packSlotCount, type FurnishPack } from './furnish_packs.js';
import type { InteriorPurpose } from './interior_purpose.js';
import {
  DESCRIPTOR_SCHEMA, ROOMS_LAYOUT_REVISION, STOREYS_LAYOUT_REVISION, descriptorDigest, hash128Hex, partitionBoxes, rngOf, roomIdOf, seedOf,
  type AccessRole, type AnchorKind, type CandidateReport, type DescAnchor, type DescDoorway, type DescFloor, type DescPartition, type DescPiece, type DescRoom, type DescUnit, type DescZone,
  type FloorLink, type InteriorDescriptor, type InteriorRecipe, type LiftCandidate, type PieceTier, type PlanReport, type PlanRung,
  type PlanStatus, type Rect, type ReservedRegion, type RouteEdge, type RouteNode, type StreamName,
} from './interior_descriptor.js';
import { KIT_VERSION, halfMm, pieceMeta, resolveKit, sideNormal, syncCatalog, boundHalf, pieceFootprintClear, pieceOffConsole, type ResolvedAnchor, type ResolvedKit, type ResolvedMember, type Side } from './interior_kits.js';
import { isKitProp } from './interior_kit_props.js';
import {
  ARCHETYPES, archetypeFor, defaultTradition, fillCount, programFor, sizeBand, storeyProgramFor,
  type Arrangement, type Fill, type FloorProgram, type InteriorArchetype, type Site, type Tradition, type UnitPlan, type ZoneSpec,
} from './interior_archetypes.js';

export const ALGORITHM_VERSION = 'planner-1';
/** K candidate programs per floor and R repair rounds per candidate — WORK budgets (report §4.3), never time. */
export const PLAN_K = 4;
export const PLAN_R = 3;
/** The route raster's cell (mm). 0.1 m: a 0.9 m gap between two solids is always found, a 0.7 m gap never is — the
 *  capsule is 0.6 m wide and wants 5 cm of air a side. A 1 m grid proves nothing about a 0.6 m body. */
export const CELL_MM = 100;
/** The capsule the route is sized for — a MIRROR of PhysicsModule's private CAPSULE_RADIUS / CAPSULE_HALF_HEIGHT (the
 *  harness pins both against that file's source) + the air kept around it. */
export const CAPSULE = { radiusMm: 300, halfHeightMm: 600, airMm: 50 } as const;
/** A shell box whose top is this far above the slab or less is walked ON (the dais, a step lip), not around. */
const STEP_MM = 400;
/** A shell box whose underside is this high clears a standing capsule. */
const HEAD_MM = 2000;
/** The slide a site may take along its wall: steps × step. */
const SLIDE_STEPS = 48, SLIDE_MM = 125;
export const DEFAULT_STAIR_ZONE: 'law' | 'shell' = 'shell';
/** How many floors the legacy plan furnishes (`furnishByPurpose`, `packFloors`). */
const LEGACY_FLOORS = 3;
/** The lift candidate's shaft (m) and the apron kept clear before its door. */
const LIFT_M = 1.6, LIFT_APRON_M = 1.1;

// ─── ROOMS (slice G, layout ≥ ROOMS_LAYOUT_REVISION) ──────────────────────────────────────────────────────────────
/** A partition's thickness (mm) — DRAWN and COLLIDED at this: THICK, OR NOT AT ALL (interiors.md law 7; the shell's
 *  two-sided guard rail is 0.4 m). A body cannot be depenetrated to the far side of a wall this thick. */
export const PARTITION_MM = 400;
/** A screen's height (mm): cover and privacy, not a room — it never closes a region. */
export const SCREEN_HEIGHT_MM = 1900;
/** A doorway's CLEAR width between the jambs (mm). The route raster frees a cell only when the whole cell is
 *  radius + air (350 mm) from every solid, so a doorway needs 700 mm + cells: 1.2 m leaves a 0.5 m band of free cells
 *  through the wall; a hall's doorway (the division of a large floor) is a double door. */
export const DOORWAY_MM = 1200;
export const HALL_DOORWAY_MM = 1800;
/** (slice U, the storeys revision) The lobby kept before a back-wall lift's door on a unit floor (mm), and the open
 *  floor a room's doorway must have before it (mm of free route cells straight out from its outer standing point —
 *  about 1.7 m of clear floor from the wall's face): a doorway never opens off a slot between two rooms. */
const LIFT_LOBBY_MM = 1800, FORECOURT_MM = 1000;
/** Ground kept clear of furniture either side of a doorway (mm), and the least wall left beside one. */
const DOOR_APRON_MM = 800, JAMB_MM = 200;
/** A floor with at least this much usable ground is DIVIDED (slice F's measure: big floors read empty). */
export const DIVIDE_M2 = 110;
/** The division's pitch (mm): a chamber is about this deep; at most this many cross walls — never a maze. */
const CHAMBER_MM = 14000, CHAMBER_WALLS = 3;
/** HOW A LARGE FLOOR OF A PROGRAM IS DIVIDED (by floor-program id):
 *   `cells` — the walls are laid BEFORE the furniture at a CELL_MM pitch and the program is seated in the rooms they
 *             make (offices, archives, wards, quarters, galleries: floors that ARE rooms off a spine);
 *   `none`  — never (a house of seats facing a stage, a nave, a cantina's one social room, a shop floor, a bay hall);
 *   absent  — `gaps`: the walls are laid AFTER the primary furniture, only in the lines it leaves free (a hall keeps
 *             its focus; a wall never moves a piece). */
export const DIVIDE_POLICY: Readonly<Record<string, 'cells' | 'none'>> = {
  'office.floor': 'cells', 'office.ground': 'cells', 'academy.study': 'cells', 'civic.archive': 'cells', 'guild.meeting': 'cells',
  'guild.craft': 'cells', 'quarters.bunks': 'cells', 'generic.upper': 'cells', 'clinic.ground': 'cells', 'clinic.ward': 'cells',
  'museum.gallery': 'cells', 'museum.upper': 'cells', 'mining.support': 'cells', 'works.store': 'cells', 'transit.operations': 'cells',
  'cantina.ground': 'none', 'cantina.lounge': 'none', 'cantina.compact': 'none', 'theater.house': 'none', 'faith.hall': 'none',
  'academy.hall': 'none', 'garage.floor': 'none', 'hive.store': 'none', 'hive.gallery': 'none', 'hive.compact': 'none', 'shop.ground': 'none',
};
const CELLS_PITCH_MM = 9000;

/** A partition while planning: axis 'x' = it runs along x at z = `at`; 'z' = along z at x = `at` (mm, centre line). */
interface Wall {
  name: string; axis: 'x' | 'z'; at: number; lo: number; hi: number; height: number; screen: boolean;
  doors: Array<{ lo: number; hi: number }>;
  /** The zone it encloses ('' = a division wall). */
  zone: string;
}

export interface OwnedPiece {
  /** The caller's stable id (`item:<structureItemId>`). */
  id: string; key: string; floor: number;
  /** Building-local metres / radians — the interior's frame (the caller converts from the structure's). */
  x: number; z: number; yaw: number;
}
export interface PlanOptions {
  stairZone?: 'law' | 'shell';
  K?: number; R?: number;
  /** Which passes run: `primary` stops before the secondary fills, `secondary` before the detail. */
  tiers?: 'primary' | 'secondary' | 'all';
  /** The layout revision slice C pins a structure to (≥ 1 for a planned layout; 0 is today's legacy plan). */
  layoutRevision?: number;
  /** Force an archetype id (the bed's lever). */
  archetype?: string;
  /** Extra salt per seed stream (the seed-independence check). */
  streamSalt?: Partial<Record<StreamName, string>>;
}
export interface PlanInput {
  /** The enterable's id EXACTLY as registered (`settle:<site>:settle:<site>:<n>`, `deed:<id>`, `mark:…`). */
  buildingId: string;
  /** The enterable's key (`deed:cantina`, `structure:civa_scifi5`). */
  key: string;
  w: number; h: number; d: number;
  purpose: InteriorPurpose;
  tradition?: Tradition;
  /** `legacy` = today's plan, untouched (the lever `atlas:interior-planner = 'off'`). */
  algorithm?: 'planner' | 'legacy';
  owned?: OwnedPiece[];
  ownedRevision?: string;
  options?: PlanOptions;
}
export interface PlanResult { descriptor: InteriorDescriptor; report: PlanReport }

// ─── Geometry in integer millimetres ─────────────────────────────────────────────────────────────────────────────────

interface Bx { x0: number; z0: number; x1: number; z1: number }
const HALF_PI = Math.PI / 2;
const mmOf = (m: number): number => Math.round(m * 1000);
const snap = (v: number): number => Math.round(v / 10) * 10;
const meets = (a: Bx, b: Bx): boolean => a.x0 < b.x1 && a.x1 > b.x0 && a.z0 < b.z1 && a.z1 > b.z0;
const inside = (a: Bx, lim: Bx): boolean => a.x0 >= lim.x0 && a.x1 <= lim.x1 && a.z0 >= lim.z0 && a.z1 <= lim.z1;
const rectOf = (b: Bx): Rect => ({ x0: b.x0 / 1000, z0: b.z0 / 1000, x1: b.x1 / 1000, z1: b.z1 / 1000 });
const gapBetween = (a: Bx, b: Bx): number => Math.hypot(Math.max(0, a.x0 - b.x1, b.x0 - a.x1), Math.max(0, a.z0 - b.z1, b.z0 - a.z1));

/** Kit-local → floor: mirror (x → −x), then `q` quarter turns of three's rotation.y. */
function xf(dx: number, dz: number, q: number, mirror: boolean): [number, number] {
  const lx = mirror ? -dx : dx;
  switch (q & 3) {
    case 0: return [lx, dz];
    case 1: return [dz, -lx];
    case 2: return [-lx, -dz];
    default: return [-dz, lx];
  }
}

interface Placed {
  key: string; x: number; z: number;
  /** Radians (the plans' convention) and, for generated pieces, the quarter turn. */
  yaw: number; q: number;
  /** AABB half extents (mm). */
  hx: number; hz: number;
  lift: number; tier: PieceTier;
  zone: string; kit: string; role: string;
  /** The kit instance it belongs to (−1 = owned / legacy). */
  inst: number;
  blocksWalk: boolean; underlay: boolean;
  onRole?: string; needRoles: string[]; optional: boolean;
  /** Assigned at the end. */
  slot: string; ownedId?: string;
  /** An owned piece's transform exactly as its owner gave it (never re-rounded on the way out). */
  exact?: { x: number; z: number };
}
const boxOf = (p: Pick<Placed, 'x' | 'z' | 'hx' | 'hz'>): Bx => ({ x0: p.x - p.hx, z0: p.z - p.hz, x1: p.x + p.hx, z1: p.z + p.hz });

// ─── The floor's base: what is there before a single piece ──────────────────────────────────────────────────────────

interface FloorBase {
  f: number; y0: number; hw: number; hd: number;       // mm
  spec: InteriorSpec; frame: PlanFrame; lawFrame: PlanFrame;
  hasRun: boolean; hasOpening: boolean; stairReserved: boolean;
  limits: Bx;
  /** Solids a blocking piece may not meet (un-inflated): shell boxes in the walking band, the console, the lift shaft, owned pieces. */
  solids: Bx[];
  /** (slice F) walked-on shell ground that is not the floor (a dais): nothing is seated on it, an underlay included. */
  walked: Bx[];
  /** Ground a blocking piece may not take: the console's standing room, the lift's apron, the landing apron, owned envelopes. */
  keep: Bx[];
  regions: Record<string, Bx>;
  /** The door corridor's depth (mm), probed from `slotClear`. */
  doorDepth: number;
  nx: number; nz: number;
  /** 1 = the capsule's centre may not be in this cell (shell only). */
  raster0: Uint8Array;
  entry: { x: number; z: number };
  nodes: Array<{ id: string; kind: RouteNode['kind']; x: number; z: number; required: boolean }>;
  usableM2: number;
  reserved: ReservedRegion[];
  owned: Placed[];
  headroom: number;
  deedGround: boolean;
  /** (slice G) the `cells` division of this floor — it depends on the shell alone, so every arrangement shares it. */
  cells?: Wall[];
}

class Raster {
  readonly cells: Uint8Array;
  constructor(readonly b: FloorBase, src: Uint8Array) { this.cells = new Uint8Array(src); }
  static index(b: FloorBase, x: number, z: number): number {
    const i = Math.floor((x + b.hw) / CELL_MM), j = Math.floor((z + b.hd) / CELL_MM);
    if (i < 0 || j < 0 || i >= b.nx || j >= b.nz) return -1;
    return j * b.nx + i;
  }
  /** Block every cell that touches `box` grown by `grow` mm. */
  static block(cells: Uint8Array, b: FloorBase, box: Bx, grow: number): void {
    const i0 = Math.max(0, Math.floor((box.x0 - grow + b.hw) / CELL_MM)), i1 = Math.min(b.nx - 1, Math.ceil((box.x1 + grow + b.hw) / CELL_MM) - 1);
    const j0 = Math.max(0, Math.floor((box.z0 - grow + b.hd) / CELL_MM)), j1 = Math.min(b.nz - 1, Math.ceil((box.z1 + grow + b.hd) / CELL_MM) - 1);
    for (let j = j0; j <= j1; j++) { const o = j * b.nx; for (let i = i0; i <= i1; i++) cells[o + i] = 1; }
  }
  /** Does `box` grown by `grow` touch a marked cell of `mask`. */
  static touches(mask: Uint8Array, b: FloorBase, box: Bx, grow: number): boolean {
    const i0 = Math.max(0, Math.floor((box.x0 - grow + b.hw) / CELL_MM)), i1 = Math.min(b.nx - 1, Math.ceil((box.x1 + grow + b.hw) / CELL_MM) - 1);
    const j0 = Math.max(0, Math.floor((box.z0 - grow + b.hd) / CELL_MM)), j1 = Math.min(b.nz - 1, Math.ceil((box.z1 + grow + b.hd) / CELL_MM) - 1);
    for (let j = j0; j <= j1; j++) { const o = j * b.nx; for (let i = i0; i <= i1; i++) if (mask[o + i]) return true; }
    return false;
  }
  centre(idx: number): { x: number; z: number } {
    const i = idx % this.b.nx, j = (idx - i) / this.b.nx;
    return { x: -this.b.hw + i * CELL_MM + CELL_MM / 2, z: -this.b.hd + j * CELL_MM + CELL_MM / 2 };
  }
}

const GROW = CAPSULE.radiusMm + CAPSULE.airMm;

/** The nearest FREE cell to a point within `maxMm` (ties: the lower index), or −1. */
function nearestFree(b: FloorBase, cells: Uint8Array, x: number, z: number, maxMm: number, dist?: Int32Array): number {
  const r = Math.ceil(maxMm / CELL_MM);
  const ci = Math.floor((x + b.hw) / CELL_MM), cj = Math.floor((z + b.hd) / CELL_MM);
  let best = -1, bestD = Infinity;
  for (let j = Math.max(0, cj - r); j <= Math.min(b.nz - 1, cj + r); j++) {
    for (let i = Math.max(0, ci - r); i <= Math.min(b.nx - 1, ci + r); i++) {
      const idx = j * b.nx + i;
      if (cells[idx] || (dist && dist[idx] < 0)) continue;
      const dx = -b.hw + i * CELL_MM + CELL_MM / 2 - x, dz = -b.hd + j * CELL_MM + CELL_MM / 2 - z;
      const d2 = dx * dx + dz * dz;
      if (d2 > maxMm * maxMm) continue;
      if (d2 < bestD) { bestD = d2; best = idx; }
    }
  }
  return best;
}

interface Flood { dist: Int32Array; parent: Int32Array; reached: number }
function flood(b: FloorBase, cells: Uint8Array, seed: number, work: PlanReport['work']): Flood {
  const n = b.nx * b.nz;
  const dist = new Int32Array(n).fill(-1), parent = new Int32Array(n).fill(-1);
  work.bfs++;
  if (seed < 0 || cells[seed]) return { dist, parent, reached: 0 };
  const queue = new Int32Array(n);
  let head = 0, tail = 0, reached = 0;
  queue[tail++] = seed; dist[seed] = 0;
  while (head < tail) {
    const c = queue[head++]; reached++;
    const i = c % b.nx;
    // a fixed neighbour order: +x, −x, +z, −z
    if (i + 1 < b.nx) { const k = c + 1; if (!cells[k] && dist[k] < 0) { dist[k] = dist[c] + 1; parent[k] = c; queue[tail++] = k; } }
    if (i > 0) { const k = c - 1; if (!cells[k] && dist[k] < 0) { dist[k] = dist[c] + 1; parent[k] = c; queue[tail++] = k; } }
    if (c + b.nx < n) { const k = c + b.nx; if (!cells[k] && dist[k] < 0) { dist[k] = dist[c] + 1; parent[k] = c; queue[tail++] = k; } }
    if (c - b.nx >= 0) { const k = c - b.nx; if (!cells[k] && dist[k] < 0) { dist[k] = dist[c] + 1; parent[k] = c; queue[tail++] = k; } }
  }
  work.cells += reached;
  return { dist, parent, reached };
}

/**
 * (slice U, the storeys revision) THE PATCH FLOOD. A unit is a room with one doorway: a piece seated INSIDE it can
 * change who reaches what only inside it. So the proof of a unit's later-pass pieces re-floods the unit's own rectangle
 * (and a rim round it: its walls, its doorway, a strip of the corridor) from the cells the previous flood reached on
 * the rim's outside — every cell outside keeps its distance and its parent. A tower's unit floor is 170,000 cells and
 * a unit 3,000: the soft layer of eight units a floor costs what one whole-floor proof did. The floor is flooded
 * whole once more when its units are dressed (`dress`), so the routes and distances the descriptor carries are exact.
 */
function floodPatch(b: FloorBase, cells: Uint8Array, prev: Flood, box: Bx, work: PlanReport['work']): Flood {
  const i0 = Math.max(0, Math.floor((box.x0 + b.hw) / CELL_MM)), i1 = Math.min(b.nx - 1, Math.ceil((box.x1 + b.hw) / CELL_MM) - 1);
  const j0 = Math.max(0, Math.floor((box.z0 + b.hd) / CELL_MM)), j1 = Math.min(b.nz - 1, Math.ceil((box.z1 + b.hd) / CELL_MM) - 1);
  const dist = new Int32Array(prev.dist), parent = new Int32Array(prev.parent);
  for (let j = j0; j <= j1; j++) { const o = j * b.nx; for (let i = i0; i <= i1; i++) { dist[o + i] = -1; parent[o + i] = -1; } }
  // the seeds: the reached cells on the ring just outside the box, nearest the entry first
  const seeds: number[] = [];
  const ring = (i: number, j: number): void => { if (i < 0 || j < 0 || i >= b.nx || j >= b.nz) return; const k = j * b.nx + i; if (!cells[k] && dist[k] >= 0) seeds.push(k); };
  for (let i = i0 - 1; i <= i1 + 1; i++) { ring(i, j0 - 1); ring(i, j1 + 1); }
  for (let j = j0; j <= j1; j++) { ring(i0 - 1, j); ring(i1 + 1, j); }
  seeds.sort((p, q) => dist[p] - dist[q] || p - q);
  const queue = new Int32Array(seeds.length + (i1 - i0 + 1) * (j1 - j0 + 1));
  let head = 0, tail = 0, visited = 0;
  for (const k of seeds) queue[tail++] = k;
  const inBox = (k: number): boolean => { const i = k % b.nx, j = (k - i) / b.nx; return i >= i0 && i <= i1 && j >= j0 && j <= j1; };
  while (head < tail) {
    const c = queue[head++];
    const i = c % b.nx;
    for (const k of [i + 1 < b.nx ? c + 1 : -1, i > 0 ? c - 1 : -1, c + b.nx < b.nx * b.nz ? c + b.nx : -1, c - b.nx]) {
      if (k < 0 || cells[k] || dist[k] >= 0 || !inBox(k)) continue;
      dist[k] = dist[c] + 1; parent[k] = c; queue[tail++] = k; visited++;
    }
  }
  work.cells += visited;
  return { dist, parent, reached: prev.reached };
}

/** Probe `slotClear` for the door corridor's depth (mm): how far along x = 0 a centre is refused. */
function probeDoorDepth(frame: PlanFrame): number {
  const hd = mmOf(frame.hd);
  for (let z = -hd + 10; z < hd - 550; z += 10) if (slotClear(frame, 0, z / 1000)) return z + hd;
  return 2 * hd;
}

/** The shaft rule a plan at `layoutRevision` is made round: the shell's own test of a deed (its KEY), the storeys
 *  revision for everything else. `interiorSpec` cuts the same shaft for the visit once the current layout is that one. */
export function liftRuleOf(key: string, layoutRevision: number): LiftShaftRule {
  return !key.startsWith('deed:') && layoutRevision >= STOREYS_LAYOUT_REVISION ? 'clear' : 'classic';
}
function liftCandidateFor(spec: InteriorSpec, deed: boolean, lawFrame: PlanFrame, rule: LiftShaftRule = 'classic'): { box: Bx; apron: Bx; wall: 'west' | 'north' | 'east'; door: 'east' | 'south' | 'west'; call: { x: number; z: number; facing: number } } | null {
  if (spec.floors < 3) return null;
  const hw = mmOf(spec.hw), hd = mmOf(spec.hd), L = mmOf(LIFT_M), A = mmOf(LIFT_APRON_M);
  const st = spec.stair;
  const cs = deed ? consoleSeat(spec) : null;
  const consoleKeep: Bx | null = cs ? { x0: mmOf(cs.x - CONSOLE_KEEP_W), x1: mmOf(cs.x + CONSOLE_KEEP_W), z0: mmOf(cs.z - CONSOLE_KEEP_FRONT), z1: mmOf(cs.z + CONSOLE_KEEP_BACK) } : null;
  const legal = (x: number, z: number): boolean => slotClear(lawFrame, x / 1000, z / 1000, 0); // off the door corridor and the stair zone (law 22's centre rule, no wall margin)
  const clear = (box: Bx, apron: Bx): boolean => {
    for (const q of [box, apron]) {
      if (consoleKeep && meets(q, consoleKeep)) return false;
      for (const [x, z] of [[q.x0, q.z0], [q.x1, q.z0], [q.x0, q.z1], [q.x1, q.z1], [(q.x0 + q.x1) / 2, (q.z0 + q.z1) / 2]]) {
        if (!legal(Math.max(-hw + 1, Math.min(hw - 1, x)), Math.max(-hd + 1, Math.min(hd - 1, z)))) return false;
      }
    }
    return true;
  };
  // 1. beside the stairs: the west wall, north of the run (when the room is that deep)
  {
    const z0 = snap(mmOf(st.z + st.d / 2 + 1.8) + 10);
    const box: Bx = { x0: -hw, x1: -hw + L, z0, z1: z0 + L };
    const apron: Bx = { x0: box.x1, x1: box.x1 + A, z0: box.z0, z1: box.z1 };
    if (box.z1 <= hd && clear(box, apron)) return { box, apron, wall: 'west', door: 'east', call: { x: box.x1 + A / 2, z: (box.z0 + box.z1) / 2, facing: -HALF_PI } };
  }
  // 2. the back wall, the first clear place east of the stair zone — under `clear` (THE CLEAR-LANDING RULE,
  //    building_interiors.ts: the shell's own rule, mirrored) also `LIFT_WAY_OUT_M` east of a stair guard it stands beside
  const W = mmOf(LIFT_WAY_OUT_M);
  const railX = mmOf(st.x + st.w / 2 + 0.41), railZ = mmOf(st.z + st.d / 2), lowX = mmOf(st.x + st.w / 2 + 0.7), lowZ = mmOf(st.z + st.d / 2 - 0.75);
  const wayOut = (box: Bx): boolean => (box.z0 >= railZ + W || box.x0 >= railX + W) && (box.z0 >= lowZ + W || box.x0 >= lowX + W);
  for (let x0 = snap(mmOf(st.x + 2.0) + 10); x0 + L <= hw; x0 += 50) {
    const box: Bx = { x0, x1: x0 + L, z0: hd - L, z1: hd };
    const apron: Bx = { x0, x1: x0 + L, z0: box.z0 - A, z1: box.z0 };
    if (rule === 'clear' && !wayOut(box)) continue;
    if (clear(box, apron)) return { box, apron, wall: 'north', door: 'south', call: { x: (box.x0 + box.x1) / 2, z: box.z0 - A / 2, facing: 0 } };
  }
  return null;
}

function buildBase(spec: InteriorSpec, f: number, input: PlanInput, stairZone: 'law' | 'shell', lift: ReturnType<typeof liftCandidateFor>, storeys = false): FloorBase {
  const hw = mmOf(spec.hw), hd = mmOf(spec.hd);
  const y0 = f === 0 ? 0 : mmOf(f * FLOOR_H);
  const hasRun = f < spec.floors - 1, hasOpening = f >= 1;
  const lawFrame: PlanFrame = { hw: spec.hw, hd: spec.hd, stair: spec.stair };
  const stairReserved = stairZone === 'law' || spec.floors > 1;
  // THE STAIR ZONE: where the shell builds no run and no opening at all, `shell` parks the frame's stair outside the
  // room — the legacy functions are still the ones asked, with a frame that says what the shell really is.
  const frame: PlanFrame = stairReserved ? lawFrame : { hw: spec.hw, hd: spec.hd, stair: { x: -spec.hw - 10, z: 0, d: 0.01, w: spec.stair.w } };
  const sink = mmOf(0.15);
  const limits: Bx = { x0: -hw - sink, x1: hw + sink, z0: -hd - sink, z1: hd + sink };
  const nx = Math.max(1, Math.ceil((2 * hw) / CELL_MM)), nz = Math.max(1, Math.ceil((2 * hd) / CELL_MM));
  const base: FloorBase = {
    f, y0, hw, hd, spec, frame, lawFrame, hasRun, hasOpening, stairReserved, limits, solids: [], walked: [], keep: [], regions: {}, doorDepth: 0,
    nx, nz, raster0: new Uint8Array(nx * nz), entry: { x: 0, z: 0 }, nodes: [], usableM2: 0, reserved: [], owned: [],
    headroom: f < spec.floors - 1 ? mmOf(FLOOR_H) - 500 : mmOf(FLOOR_H), deedGround: f === 0 && input.buildingId.startsWith('deed:'),
  };
  const cells = base.raster0;
  // The floor under the feet: above the ground a cell is floor only where a slab or a landing carries it.
  let floored: Uint8Array | null = null;
  if (f >= 1) {
    cells.fill(1);
    for (const b of spec.boxes) {
      if (b.mat !== 'floor' || !b.collide) continue;
      const top = mmOf(b.y + b.h / 2);
      if (Math.abs(top - y0) > 60) continue;
      const bx: Bx = { x0: mmOf(b.x - b.w / 2), x1: mmOf(b.x + b.w / 2), z0: mmOf(b.z - b.d / 2), z1: mmOf(b.z + b.d / 2) };
      const i0 = Math.max(0, Math.ceil((bx.x0 + hw - CELL_MM / 2) / CELL_MM)), i1 = Math.min(nx - 1, Math.floor((bx.x1 + hw - CELL_MM / 2) / CELL_MM));
      const j0 = Math.max(0, Math.ceil((bx.z0 + hd - CELL_MM / 2) / CELL_MM)), j1 = Math.min(nz - 1, Math.floor((bx.z1 + hd - CELL_MM / 2) / CELL_MM));
      for (let j = j0; j <= j1; j++) for (let i = i0; i <= i1; i++) cells[j * nx + i] = 0;
    }
    floored = new Uint8Array(nx * nz);
    for (let k = 0; k < floored.length; k++) floored[k] = cells[k] ? 0 : 1;
  }
  // The shell's solids in the walking band.
  for (const b of spec.boxes) {
    if (!b.collide) continue;
    const top = mmOf(b.y + b.h / 2), bottom = mmOf(b.y - b.h / 2);
    if (top <= y0 + STEP_MM || bottom >= y0 + HEAD_MM) continue;
    const bx: Bx = { x0: mmOf(b.x - b.w / 2), x1: mmOf(b.x + b.w / 2), z0: mmOf(b.z - b.d / 2), z1: mmOf(b.z + b.d / 2) };
    Raster.block(cells, base, bx, GROW);
    // a piece may not stand in a shell solid — the perimeter sheets excepted (the wall sink is the legacy functions')
    const perimeter = b.mat === 'wall' && b.render === false;
    if (!perimeter) { base.solids.push(bx); if (b.mat !== 'stair') base.reserved.push({ kind: 'shell', floor: f, rect: rectOf(bx), label: b.mat }); }
  }
  // The legacy reservations, PROBED from the functions (the debug view and the regions).
  base.doorDepth = probeDoorDepth(frame);
  const doorZ = Math.min(hd, -hd + base.doorDepth);
  const doorHalf = mmOf(DOOR_BOX_HALF);
  base.reserved.push({ kind: 'door', floor: f, rect: { x0: -DOOR_BOX_HALF, x1: DOOR_BOX_HALF, z0: -spec.hd, z1: doorZ / 1000 }, label: f === 0 ? 'door corridor' : 'door corridor (law 22, every floor)' });
  let xW = -hw;
  if (stairReserved) {
    const edge = mmOf(stairEdgeX(lawFrame));
    xW = Math.min(hw, snap(edge + 20));
    const st = spec.stair;
    base.reserved.push({ kind: 'stair', floor: f, rect: { x0: -spec.hw, x1: edge / 1000, z0: Math.max(-spec.hd, st.z - st.d / 2 - 1.2), z1: Math.min(spec.hd, st.z + st.d / 2 + 0.9) }, label: hasRun || hasOpening ? 'stair zone' : 'stair zone (law 22; no run in this shell)' });
  }
  // (slice F) A WALKED-ON shell box that is not the floor — the guild hall's dais and its step lip — is ground a body
  // crosses and furniture must not take: a piece seated there would stand half-buried in it. Outside the stair zone
  // only (the ramp's low slices are walked on too, and that ground is reserved already).
  {
    const st = spec.stair, edge = stairEdgeX(lawFrame);
    for (const b of spec.boxes) {
      if (!b.collide || b.mat === 'floor' || b.mat === 'wall') continue;
      const top = mmOf(b.y + b.h / 2);
      if (top <= y0 + 60 || top > y0 + STEP_MM || mmOf(b.y - b.h / 2) > y0 + 60) continue;
      if (spec.floors > 1 && b.x - b.w / 2 < edge && b.z + b.d / 2 > st.z - st.d / 2 - 1.2 && b.z - b.d / 2 < st.z + st.d / 2 + 0.9) continue;
      const bx: Bx = { x0: mmOf(b.x - b.w / 2), x1: mmOf(b.x + b.w / 2), z0: mmOf(b.z - b.d / 2), z1: mmOf(b.z + b.d / 2) };
      base.keep.push(bx); base.walked.push(bx);
      base.reserved.push({ kind: 'shell', floor: f, rect: rectOf(bx), label: 'walked-on shell (dais)' });
    }
  }
  const region = (name: string, b: Bx): void => { if (b.x1 - b.x0 >= 400 && b.z1 - b.z0 >= 400) base.regions[name] = b; };
  region('room', { x0: -hw, x1: hw, z0: -hd, z1: hd });
  region('main', { x0: xW, x1: hw, z0: doorZ, z1: hd });
  region('frontEast', { x0: doorHalf, x1: hw, z0: -hd, z1: doorZ });
  region('frontWest', { x0: xW, x1: -doorHalf, z0: -hd, z1: doorZ });
  region('mainEast', { x0: doorHalf, x1: hw, z0: doorZ, z1: hd });
  region('mainWest', { x0: xW, x1: -doorHalf, z0: doorZ, z1: hd });
  region('east', { x0: doorHalf, x1: hw, z0: -hd, z1: hd });
  region('west', { x0: xW, x1: -doorHalf, z0: -hd, z1: hd });
  // The west "wall" of the room is the stair rail's line where the stairs are reserved.
  if (base.regions.room && xW > -hw) base.regions.room = { ...base.regions.room, x0: xW };
  // The structure console (a deed's ground floor): its box is solid, its standing room is kept.
  if (base.deedGround) {
    const c = consoleSeat(spec);
    const cx = mmOf(c.x), cz = mmOf(c.z);
    const box: Bx = { x0: cx - 450, x1: cx + 450, z0: cz - 350, z1: cz + 350 };
    const keep: Bx = { x0: cx - mmOf(CONSOLE_KEEP_W), x1: cx + mmOf(CONSOLE_KEEP_W), z0: cz - mmOf(CONSOLE_KEEP_FRONT), z1: cz + mmOf(CONSOLE_KEEP_BACK) };
    base.solids.push(box); base.keep.push(keep);
    Raster.block(cells, base, box, GROW);
    base.reserved.push({ kind: 'console', floor: f, rect: rectOf(keep), label: 'structure console + standing room' });
    base.nodes.push({ id: 'console', kind: 'console', x: cx, z: cz - 1000, required: true });
  }
  // The lift candidate: the shaft is solid, its apron kept, the call post a node.
  if (lift) {
    base.solids.push(lift.box); base.keep.push(lift.apron);
    Raster.block(cells, base, lift.box, GROW);
    base.reserved.push({ kind: 'lift', floor: f, rect: rectOf(lift.box), label: 'lift shaft (candidate)' });
    base.reserved.push({ kind: 'lift', floor: f, rect: rectOf(lift.apron), label: 'lift apron' });
    // (slice U) from the storeys revision on the call post is a REQUIRED node on every floor: a plan whose furniture or
    // walls cut the lift's landing off from the room is not a plan (below it the post could be walled in — it was, on
    // the ground floor of 3 % of the towers)
    base.nodes.push({ id: 'lift-call', kind: 'lift-call', x: lift.call.x, z: lift.call.z, required: storeys });
  }
  // Stairs: the foot of the run up, the landing of the run below.
  const st = spec.stair;
  if (hasRun) base.nodes.push({ id: 'stair-foot', kind: 'stair-foot', x: mmOf(st.x), z: mmOf(st.z - st.d / 2 - 0.5), required: true });
  if (hasOpening) {
    const apron: Bx = { x0: mmOf(st.x + st.w / 2 + 0.1), x1: mmOf(st.x + st.w / 2 + 1.3), z0: mmOf(st.z + st.d / 2 - 0.75), z1: mmOf(st.z + st.d / 2 + 0.75) };
    base.keep.push(apron);
    base.reserved.push({ kind: 'landing', floor: f, rect: rectOf(apron), label: 'landing apron' });
  }
  // Owned pieces: locked content — solid, with the standing room before each kept.
  for (const o of input.owned ?? []) {
    if (o.floor !== f) continue;
    const meta = pieceMeta(o.key);
    if (!meta) continue;
    const h = drawnHalf(o.key, o.yaw);
    const p: Placed = {
      key: o.key, x: mmOf(o.x), z: mmOf(o.z), yaw: o.yaw, q: 0, hx: Math.ceil(h.hx * 1000 - 1e-6), hz: Math.ceil(h.hz * 1000 - 1e-6), lift: 0, tier: 'owned',
      zone: 'owned', kit: 'owned', role: o.key, inst: -1, blocksWalk: meta.blocksWalk, underlay: meta.underlay, needRoles: [], optional: false, slot: o.id, ownedId: o.id, exact: { x: o.x, z: o.z },
    };
    base.owned.push(p);
    if (!p.blocksWalk) continue;
    const bx = boxOf(p);
    base.solids.push(bx);
    Raster.block(cells, base, bx, GROW);
    base.reserved.push({ kind: 'owned', floor: f, rect: rectOf(bx), label: o.id });
    if (meta.use) {
      const fx = Math.sin(o.yaw), fz = Math.cos(o.yaw); // the piece's front (+Z at yaw 0)
      const reachMm = Math.max(p.hx, p.hz) + 450;
      const k: Bx = { x0: p.x + fx * reachMm - 350, x1: p.x + fx * reachMm + 350, z0: p.z + fz * reachMm - 350, z1: p.z + fz * reachMm + 350 };
      base.keep.push(k);
      base.reserved.push({ kind: 'owned', floor: f, rect: rectOf(k), label: `${o.id} — use envelope` });
    }
  }
  // The entry: the door on the ground floor, the landing above.
  const seedPt = f === 0 ? { x: 0, z: -hd + 150 } : { x: mmOf(st.x), z: mmOf(st.z + st.d / 2 + 0.3) };
  const eIdx = nearestFree(base, cells, seedPt.x, seedPt.z, f === 0 ? 1200 : 1800);
  if (eIdx >= 0) { const i = eIdx % nx, j = (eIdx - i) / nx; base.entry = { x: -hw + i * CELL_MM + CELL_MM / 2, z: -hd + j * CELL_MM + CELL_MM / 2 }; } else base.entry = seedPt;
  // Usable area: the cells a piece's CENTRE may take (the legacy centre rule) that are floor and not reserved.
  // Usable area (report §2.2): the floor that is walkable ground after the shell, the stairs and the reservations —
  // the room, less the door corridor's box, the stair zone, the shell's solids and every kept ground.
  const taken = new Uint8Array(nx * nz);
  for (const q of [...base.solids, ...base.keep]) Raster.block(taken, base, q, 0);
  for (const r of base.reserved) if (r.kind === 'door' || r.kind === 'stair') Raster.block(taken, base, { x0: mmOf(r.rect.x0), x1: mmOf(r.rect.x1), z0: mmOf(r.rect.z0), z1: mmOf(r.rect.z1) }, 0);
  let usable = 0;
  for (let k = 0; k < taken.length; k++) if (!taken[k] && (!floored || floored[k])) usable++;
  base.usableM2 = (usable * CELL_MM * CELL_MM) / 1e6;
  return base;
}

// ─── A candidate: kits seated on a base ──────────────────────────────────────────────────────────────────────────────

interface KitInst {
  id: number; zone: string; kit: ResolvedKit; ox: number; oz: number; q: number; mirror: boolean;
  pass: 'primary' | 'secondary' | 'detail'; unit: number;
  /** Seated members by role. */
  seated: Map<string, Placed>;
  /** Reserved anchors that held. */
  reserved: Set<number>;
}
interface PendingAnchor {
  inst: number; zone: string; kit: string; a: ResolvedAnchor; member: Placed;
  /** Floor-frame points (mm): the thing, the preferred standing point; the envelope's half extents. */
  px: number; pz: number; ax: number; az: number; ehx: number; ehz: number;
}
interface Cand {
  base: FloorBase;
  program: FloorProgram; arrangement: Arrangement;
  placed: Placed[]; insts: KitInst[]; keeps: Bx[];
  zones: Map<string, ZoneSpec>; zoneRects: Map<string, Bx[]>; dropped: string[];
  cursors: Map<string, { lo: number; hi: number }>;
  regions: Record<string, Bx>;
  reasons: string[];
  units: Array<{ index: number; rect: Bx; zone: string; inner?: Bx }>;
  repairs: number;
  /** (slice G) the partitions, their solid boxes (the walls less their doorways) and the regions they were meant to close. */
  walls: Wall[]; wallBoxes: Bx[]; enclosed: Array<{ name: string; x: number; z: number; access: AccessRole }>;
  /** The floor was divided before it was furnished (`cells`). */
  divided: boolean;
  /** (slice U) this candidate's fills take their leading wall sites in the storey's own seeded order (`runFill`). */
  turn: number;
}

interface Ctx {
  input: PlanInput; spec: InteriorSpec; work: PlanReport['work'];
  seedNs: string; salt: Partial<Record<StreamName, string>>; tiers: 'primary' | 'secondary' | 'all';
  K: number; R: number; tradition: Tradition;
  /** (slice G) build the enclosures: layout ≥ ROOMS_LAYOUT_REVISION. */
  rooms: boolean;
  /** (slice U) THE STOREYS REVISION: layout ≥ STOREYS_LAYOUT_REVISION — a program for every storey, the compact rung
   *  above the ground, the legacy fallback clear of the lift, the unit floors' soft layer and lobby. */
  storeys: boolean;
  /** (slice U) the candidates being evaluated take their sites in the seeded order (set per evaluation by `planInterior`). */
  turnSites: number;
}

function pieceFits(c: Cand, key: string, x: number, z: number, q: number, opts: { blocks: boolean; underlay: boolean; onTop: boolean; own?: Placed[] }): boolean {
  const b = c.base;
  const h = halfMm(key, q);
  const bx: Bx = { x0: x - h.hx, x1: x + h.hx, z0: z - h.hz, z1: z + h.hz };
  if (!inside(bx, b.limits)) return false;
  const xm = x / 1000, zm = z / 1000;
  // (slice F) a PROCEDURAL prop has no catalog entry: its own box answers law 38 and the console's ground
  const prop = isKitProp(key);
  if (!slotClear(b.frame, xm, zm) || !(prop ? pieceFootprintClear(b.frame, key, xm, zm, q * HALF_PI) : footprintClear(b.frame, key, xm, zm, q * HALF_PI))) return false;
  const meta = pieceMeta(key);
  if (!meta) return false;
  // THE CONSOLE'S GROUND, asked of the function the pack path uses: a slot `offTheConsole` would drop is never seated
  if (b.deedGround && (prop ? !pieceOffConsole(b.lawFrame, key, xm, zm, q * HALF_PI) : offTheConsole([{ key, x: xm, z: zm, yaw: q * HALF_PI }], b.lawFrame).length === 0)) return false;
  if (opts.onTop) return true; // it stands on a piece that already passed
  if (meta.drawn.h * 1000 > b.headroom - 50) return false;
  for (const s of b.solids) if (meets(bx, s)) return false;
  for (const s of b.walked) if (meets(bx, s)) return false;
  for (const s of c.wallBoxes) if (meets(bx, s)) return false; // (slice G) nothing stands in a partition, a rug included
  if (opts.underlay) return true; // a rug lies under anything but never over a shell solid or the stairwell
  for (const k of b.keep) if (meets(bx, k)) return false;
  for (const k of c.keeps) if (meets(bx, k)) return false;
  for (const p of c.placed) {
    if (!p.blocksWalk || (opts.own && opts.own.includes(p))) continue;
    if (meets(bx, boxOf(p))) return false;
  }
  return true;
}

/** Seat a kit instance at (ox, oz, q, mirror); null when a member it cannot do without does not fit. */
function tryPlace(ctx: Ctx, c: Cand, zone: string, kit: ResolvedKit, ox: number, oz: number, q: number, mirror: boolean, pass: KitInst['pass'], sparse: boolean, unit: number, leave?: Set<string>): KitInst | null {
  ctx.work.tries++;
  const seated = new Map<string, Placed>();
  const fresh: Placed[] = [];
  const id = c.insts.reduce((mx, i) => Math.max(mx, i.id), -1) + 1;
  // `leave` = a wall run's STRICT try: the members it names stay out, every other member must seat
  const strict = !!leave;
  for (const m of kit.members) {
    if (sparse && m.optional) continue;
    if (leave?.has(m.role)) continue;
    if (m.needs.some((r) => !seated.has(r))) { if (!m.optional || (strict && m.blocksWalk)) return null; continue; }
    const [dx, dz] = xf(m.dx, m.dz, q, mirror);
    const x = snap(ox + dx), z = snap(oz + dz);
    const turn = mirror ? (4 - m.turn) & 3 : m.turn;
    const wq = (q + turn) & 3;
    const meta = pieceMeta(m.key);
    if (!meta) { if (!m.optional) return null; continue; }
    const mustSeat = !m.optional || (strict && m.blocksWalk);
    const ok = pieceFits(c, m.key, x, z, wq, { blocks: m.blocksWalk, underlay: meta.underlay || (!m.blocksWalk && !m.on), onTop: !!m.on, own: fresh });
    // a blocking member may not meet its own kit-mates either
    const clash = ok && m.blocksWalk && fresh.some((p) => p.blocksWalk && meets(boxOf(p), { x0: x - halfMm(m.key, wq).hx, x1: x + halfMm(m.key, wq).hx, z0: z - halfMm(m.key, wq).hz, z1: z + halfMm(m.key, wq).hz }));
    if (!ok || clash) { if (mustSeat) return null; continue; }
    const h = halfMm(m.key, wq);
    const p: Placed = {
      key: m.key, x, z, yaw: wq * HALF_PI, q: wq, hx: h.hx, hz: h.hz, lift: m.lift, tier: pass === 'primary' ? m.tier : pass === 'secondary' ? (m.tier === 'detail' ? 'detail' : 'secondary') : 'detail',
      zone, kit: kit.spec.id, role: m.role, inst: id, blocksWalk: m.blocksWalk, underlay: !m.blocksWalk && !m.on, onRole: m.on, needRoles: m.needs, optional: m.optional, slot: '',
    };
    seated.set(m.role, p); fresh.push(p);
  }
  if (fresh.length === 0) return null;
  // Reserved anchors: the standing box must be free ground, now and for good.
  const reserved = new Set<number>();
  const newKeeps: Bx[] = [];
  for (const a of kit.anchors) {
    if (!a.spec.reserve) continue;
    if (!seated.has(a.spec.of)) { if (a.spec.required) return null; continue; }
    const [ax, az] = xf(a.ax, a.az, q, mirror);
    const odd = (q & 1) === 1;
    const env: Bx = { x0: ox + ax - (odd ? a.ehz : a.ehx), x1: ox + ax + (odd ? a.ehz : a.ehx), z0: oz + az - (odd ? a.ehx : a.ehz), z1: oz + az + (odd ? a.ehx : a.ehz) };
    let free = env.x0 >= -c.base.hw + 100 && env.x1 <= c.base.hw - 100 && env.z0 >= -c.base.hd + 100 && env.z1 <= c.base.hd - 100;
    if (free) for (const s of c.base.solids) if (meets(env, s)) { free = false; break; }
    if (free) for (const s of c.wallBoxes) if (meets(env, s)) { free = false; break; }
    if (free) for (const p of c.placed) if (p.blocksWalk && meets(env, boxOf(p))) { free = false; break; }
    if (free) for (const p of fresh) if (p.blocksWalk && meets(env, boxOf(p))) { free = false; break; }
    if (!free) { if (a.spec.required) return null; continue; }
    reserved.add(a.index); newKeeps.push(env);
  }
  // Every REQUIRED anchor's member must be seated.
  for (const a of kit.anchors) if (a.spec.required && !seated.has(a.spec.of)) return null;
  const inst: KitInst = { id, zone, kit, ox, oz, q, mirror, pass, unit, seated, reserved };
  c.insts.push(inst);
  c.placed.push(...fresh);
  c.keeps.push(...newKeeps);
  ctx.work.placements++;
  return inst;
}

/** The kit's blocking box after a turn / mirror, relative to its origin. */
function kitBoxAt(kit: ResolvedKit, q: number, mirror: boolean): Bx {
  const pts: Array<[number, number]> = [xf(kit.box.x0, kit.box.z0, q, mirror), xf(kit.box.x1, kit.box.z1, q, mirror)];
  return { x0: Math.min(pts[0][0], pts[1][0]), x1: Math.max(pts[0][0], pts[1][0]), z0: Math.min(pts[0][1], pts[1][1]), z1: Math.max(pts[0][1], pts[1][1]) };
}

/** The box of the kit's blocking members less the ones left out, after a turn / mirror, relative to its origin. */
function boxOfMembers(kit: ResolvedKit, q: number, mirror: boolean, leave: Set<string>): Bx {
  let b: Bx | null = null;
  for (const m of kit.members) {
    // (slice F) a procedural piece counts by its footprint whether or not it blocks a walk (resolveKit's box rule): a
    // stage is centred by its RISER, not by whichever drape was seated
    if ((!m.blocksWalk && !(isKitProp(m.key) && !m.on)) || leave.has(m.role)) continue;
    const [x, z] = xf(m.dx, m.dz, q, mirror);
    const odd = (q & 1) === 1;
    const hx = odd ? m.hz : m.hx, hz = odd ? m.hx : m.hz;
    const mb: Bx = { x0: x - hx, x1: x + hx, z0: z - hz, z1: z + hz };
    b = b ? { x0: Math.min(b.x0, mb.x0), x1: Math.max(b.x1, mb.x1), z0: Math.min(b.z0, mb.z0), z1: Math.max(b.z1, mb.z1) } : mb;
  }
  return b ?? kitBoxAt(kit, q, mirror);
}
/** The box of a seated instance's blocking pieces. */
function instBox(inst: KitInst): Bx {
  let b: Bx | null = null;
  for (const p of inst.seated.values()) {
    if (!p.blocksWalk) continue;
    const q = boxOf(p);
    b = b ? { x0: Math.min(b.x0, q.x0), x1: Math.max(b.x1, q.x1), z0: Math.min(b.z0, q.z0), z1: Math.max(b.z1, q.z1) } : q;
  }
  return b ?? { x0: inst.ox, x1: inst.ox, z0: inst.oz, z1: inst.oz };
}

const WALL_Q: Record<'N' | 'E' | 'S' | 'W', number> = { S: 0, W: 1, N: 2, E: 3 };

/** Seat `kit` at a site; returns the instance or null. */
function placeAtSite(ctx: Ctx, c: Cand, zone: string, kit: ResolvedKit, site: Site, pass: KitInst['pass'], sparse: boolean, unit: number, mirrorSeed: boolean): KitInst | null {
  const R = c.regions[site.in];
  if (!R) return null;
  const b = c.base;
  if (site.mode === 'wall' || site.mode === 'row') {
    const w = site.wall ?? 'N', q = WALL_Q[w], align = site.align ?? 'start';
    const alongX = w === 'N' || w === 'S';
    const plane = w === 'N' ? R.z1 : w === 'S' ? R.z0 : w === 'E' ? R.x1 : R.x0;
    const roomWall = (w === 'N' && plane === b.hd) || (w === 'S' && plane === -b.hd) || (w === 'E' && plane === b.hw) || (w === 'W' && plane === -b.hw);
    // how far the kit's origin stands from the wall line
    let D = 0;
    if (site.mode === 'row') D = mmOf(site.off ?? 1.2) - kit.box.z0;
    else {
      for (const m of kit.members) {
        if (m.on) continue;
        D = Math.max(D, roomWall ? Math.max(560 - m.dz, m.hz - m.dz - 140) : m.hz - m.dz);
      }
      D = Math.ceil(D / 10) * 10;
    }
    const inward = w === 'N' || w === 'E' ? -1 : 1;
    const fixed = plane + inward * D;
    // the run's range: a piece may sink into a side wall as far as the legacy rule lets it
    const sink = 150;
    const lo = (alongX ? R.x0 : R.z0) - ((alongX ? R.x0 === -b.hw : R.z0 === -b.hd) ? sink : 0);
    const hi = (alongX ? R.x1 : R.z1) + ((alongX ? R.x1 === b.hw : R.z1 === b.hd) ? sink : 0);
    const key = `${site.in}:${w}:${site.mode}`;
    const cur = c.cursors.get(key) ?? { lo, hi };
    // a wall kit's open side faces the rest of the run: its LEFT is +along on N / W, −along on S / E
    const leftAlong = w === 'N' || w === 'W' ? 1 : -1;
    const want = align === 'end' ? -1 : 1;
    const firstMirror = site.mode === 'row' ? mirrorSeed : leftAlong !== want;
    const margin = site.mode === 'row' && align !== 'center' ? 1300 : 0;
    // NEAR FIRST, WHOLE FIRST: the whole kit by its site (either hand), then the kit less its LAST optional member, less
    // its last two … — each try strict (every member it still names must seat, so nothing is left with a gap where a
    // side table should be) — and only then does anything slide far. A bed stays in its corner without its side table
    // before it crosses the room with one.
    const optional = kit.members.filter((m) => m.optional && m.blocksWalk).map((m) => m.role);
    const attempt = (mirror: boolean, dropN: number, k0: number, k1: number): KitInst | null => {
      const leave = new Set<string>(optional.slice(optional.length - dropN));
      // whatever hangs off a left-out member is left out too
      for (let grew = true; grew;) { grew = false; for (const m of kit.members) if (!leave.has(m.role) && m.needs.some((r) => leave.has(r))) { leave.add(m.role); grew = true; } }
      const kb = boxOfMembers(kit, q, mirror, leave);
      const a0 = alongX ? kb.x0 : kb.z0, a1 = alongX ? kb.x1 : kb.z1;
      for (let k = k0; k < k1; k++) {
        let along: number;
        // (slice F) onAxis: 'center' along a north / south wall is the DOOR's axis (x = 0), where the aisle runs — the
        // run's own middle sits off it wherever the stair strip narrows the room
        if (align === 'center') { const mid = snap((site.onAxis && alongX ? 0 : (cur.lo + cur.hi) / 2) - (a0 + a1) / 2); along = k === 0 ? mid : mid + (k % 2 === 1 ? 1 : -1) * Math.ceil(k / 2) * SLIDE_MM; }
        else if (align === 'start') along = Math.ceil((cur.lo + margin - a0) / 10) * 10 + k * SLIDE_MM;
        else along = Math.floor((cur.hi - margin - a1) / 10) * 10 - k * SLIDE_MM;
        if (along + a0 < lo || along + a1 > hi) { if (align === 'center') continue; break; }
        const inst = alongX ? tryPlace(ctx, c, zone, kit, along, fixed, q, mirror, pass, sparse, unit, leave) : tryPlace(ctx, c, zone, kit, fixed, along, q, mirror, pass, sparse, unit, leave);
        if (inst) {
          const gap = mmOf(site.gap ?? 0.12);
          const ib = instBox(inst);
          if (align === 'start') c.cursors.set(key, { lo: (alongX ? ib.x1 : ib.z1) + gap, hi: cur.hi });
          else if (align === 'end') c.cursors.set(key, { lo: cur.lo, hi: (alongX ? ib.x0 : ib.z0) - gap });
          return inst;
        }
      }
      return null;
    };
    const NEAR = 10;
    const levels = sparse ? [optional.length] : Array.from({ length: optional.length + 1 }, (_, n) => n);
    // (slice F) a FIXED site takes its exact place or none: a focus is centred on its wall, never slid beside what holds the centre
    for (const [k0, k1] of (site.fixed ? [[0, 1]] : [[0, NEAR], [NEAR, SLIDE_STEPS]]) as ReadonlyArray<readonly [number, number]>) {
      for (const dropN of levels) {
        const got = attempt(firstMirror, dropN, k0, k1) ?? attempt(!firstMirror, dropN, k0, k1);
        if (got) return got;
      }
    }
    return null;
  }
  if (site.mode === 'spot') {
    const q = site.turn ?? 0;
    const kb = kitBoxAt(kit, q, false);
    const tx = snap(R.x0 + (site.u ?? 0.5) * (R.x1 - R.x0) - (kb.x0 + kb.x1) / 2), tz = snap(R.z0 + (site.v ?? 0.5) * (R.z1 - R.z0) - (kb.z0 + kb.z1) / 2);
    const ring: Array<[number, number]> = [[0, 0]];
    for (let r = 1; r <= 5; r++) for (const [sx, sz] of [[1, 0], [-1, 0], [0, 1], [0, -1], [1, 1], [-1, 1], [1, -1], [-1, -1]] as const) ring.push([sx * r * 250, sz * r * 250]);
    for (const [dx, dz] of ring) {
      const inst = tryPlace(ctx, c, zone, kit, tx + dx, tz + dz, q, false, pass, sparse, unit);
      if (inst) return inst;
    }
    return null;
  }
  return null;
}

/** A grid of islands across a region: up to `count` instances, the cells nearest the room's axis first. */
function placeGrid(ctx: Ctx, c: Cand, zone: string, kits: ResolvedKit[], site: Site, count: number, pass: KitInst['pass'], sparse: boolean, rng: ReturnType<typeof rngOf>): number {
  const R = c.regions[site.in];
  if (!R || kits.length === 0) return 0;
  const q = site.turn ?? 0, gap = mmOf(site.gap ?? 1.1), margin = mmOf(site.margin ?? 0.3);
  const kb = kitBoxAt(kits[0], q, false);
  const w = kb.x1 - kb.x0, d = kb.z1 - kb.z0;
  const W = R.x1 - R.x0 - 2 * margin, Dp = R.z1 - R.z0 - 2 * margin;
  const cols = Math.max(0, Math.floor((W + gap) / (w + gap))), rows = Math.max(0, Math.floor((Dp + gap) / (d + gap)));
  if (cols === 0 || rows === 0) return 0;
  const padX = (W - cols * w - (cols - 1) * gap) / 2, padZ = (Dp - rows * d - (rows - 1) * gap) / 2;
  const cellsList: Array<{ x: number; z: number; key: number }> = [];
  for (let r = 0; r < rows; r++) for (let k = 0; k < cols; k++) {
    const x = R.x0 + margin + padX + k * (w + gap) + w / 2, z = R.z0 + margin + padZ + r * (d + gap) + d / 2;
    cellsList.push({ x, z, key: Math.round(Math.abs(x)) * 100000 + Math.round(z + c.base.hd) });
  }
  cellsList.sort((a, b2) => a.key - b2.key);
  let n = 0;
  for (const cell of cellsList) {
    if (n >= count) break;
    // the primary stream may seat the smaller variant at a cell (a mix of fours and twos), never changes the count
    const order = kits.length > 1 && rng.chance(1, 4) ? [...kits.slice(1), kits[0]] : kits;
    let done = false;
    for (const kit of order) {
      const b2 = kitBoxAt(kit, q, false);
      // the cell, then a short shuffle about it (a fixed list — work, not luck)
      for (const [sx, sz] of [[0, 0], [0, -300], [0, 300], [-300, 0], [300, 0], [0, -600], [0, 600]] as const) {
        if (tryPlace(ctx, c, zone, kit, snap(cell.x + sx - (b2.x0 + b2.x1) / 2), snap(cell.z + sz - (b2.z0 + b2.z1) / 2), q, false, pass, sparse, -1)) { n++; done = true; break; }
      }
      if (done) break;
    }
  }
  return n;
}

function runFill(ctx: Ctx, c: Cand, f: Fill, zoneId: string, pass: KitInst['pass'], sparse: boolean, unit: number): number {
  const stream: StreamName = pass === 'primary' ? 'primary' : pass;
  const rng = rngOf(seedOf(ctx.input.buildingId, ctx.seedNs + (ctx.salt[stream] ?? ''), c.base.f, `${zoneId}/${f.kits.join('+')}`, stream));
  let kitIds = f.kits;
  if (f.pick === 'seed' && kitIds.length > 1) { const r = rng.int(kitIds.length); kitIds = [...kitIds.slice(r), ...kitIds.slice(0, r)]; }
  const kits = kitIds.map((k) => resolveKit(k)).filter((k): k is ResolvedKit => !!k);
  if (kits.length === 0) return 0;
  // lived-in detail is not everywhere: a seeded quarter of the detail fills stay out
  if (pass === 'detail' && rng.chance(1, 4)) return 0;
  const want = sparse ? 1 : fillCount(f, c.base.usableM2);
  const mirrorSeed = rng.chance(1, 2);
  // (slice U, the storeys revision) SIBLINGS, NOT COPIES: above the ground a fill's leading WALL sites (up to three)
  // take turns by the storey's own seed — the bed is on the east wall on one storey and the back wall on the next. All
  // are sites the program's author named; a fixed (focus) site, a grid and a spot keep their place.
  let sites = f.sites;
  if (c.turn > 0) {
    let m = 0;
    while (m < 3 && m < sites.length && sites[m].mode === 'wall' && !sites[m].fixed) m++;
    // the turn has its own stream (turn 1, turn 2 …): a second look at a storey is a different order, never the same one
    const r = m >= 2 ? rngOf(seedOf(ctx.input.buildingId, ctx.seedNs, c.base.f, `${zoneId}/${f.kits.join('+')}/${unit}/turn${c.turn}`, 'topology')).int(m) : 0;
    if (r > 0) sites = [...sites.slice(r, m), ...sites.slice(0, r), ...sites.slice(m)];
  }
  let n = 0;
  for (const site of sites) {
    if (n >= want) break;
    if (site.mode === 'grid') { n += placeGrid(ctx, c, zoneId, kits, site, want - n, pass, sparse, rng); continue; }
    // one after another along the site until it takes no more
    for (let guard = 0; guard < want && n < want; guard++) {
      let got: KitInst | null = null;
      for (const kit of kits) { got = placeAtSite(ctx, c, zoneId, kit, site, pass, sparse, unit, mirrorSeed); if (got) break; }
      if (!got) break;
      n++;
    }
  }
  return n;
}

// ─── Anchors and the route ───────────────────────────────────────────────────────────────────────────────────────────

interface Resolved {
  cells: Uint8Array; fl: Flood;
  anchors: Array<DescAnchor & { inst: number; member: Placed | null; required: boolean; cell: number }>;
  failed: Array<{ inst: number; member: Placed; required: boolean; kind: AnchorKind; zone: string }>;
  nodeCells: Map<string, number>;
  missingNodes: string[];
}

function rasterOf(c: Cand): Uint8Array {
  const cells = new Uint8Array(c.base.raster0);
  for (const p of c.placed) if (p.blocksWalk) Raster.block(cells, c.base, boxOf(p), GROW);
  for (const s of c.wallBoxes) Raster.block(cells, c.base, s, GROW); // (slice G) a doorway is proven by the SAME raster
  return cells;
}

function resolveAnchors(ctx: Ctx, c: Cand, patch?: { prev: Resolved; box: Bx }): Resolved {
  const b = c.base;
  const cells = rasterOf(c);
  const seed = Raster.index(b, b.entry.x, b.entry.z);
  const fl = patch ? floodPatch(b, cells, patch.prev.fl, patch.box, ctx.work) : flood(b, cells, seed >= 0 && !cells[seed] ? seed : nearestFree(b, cells, b.entry.x, b.entry.z, 1500), ctx.work);
  const out: Resolved = { cells, fl, anchors: [], failed: [], nodeCells: new Map(), missingNodes: [] };
  for (const n of b.nodes) {
    const idx = nearestFree(b, cells, n.x, n.z, 900, fl.dist);
    if (idx >= 0) out.nodeCells.set(n.id, idx); else out.missingNodes.push(n.id);
  }
  const counter = new Map<string, number>();
  const centreOf = (idx: number): { x: number; z: number } => { const i = idx % b.nx, j = (idx - i) / b.nx; return { x: -b.hw + i * CELL_MM + CELL_MM / 2, z: -b.hd + j * CELL_MM + CELL_MM / 2 }; };
  for (const inst of c.insts) {
    for (const a of inst.kit.anchors) {
      const member = inst.seated.get(a.spec.of);
      if (!member) continue;
      const meta = pieceMeta(member.key);
      if (!meta) continue;
      const [ax0, az0] = xf(a.ax, a.az, inst.q, inst.mirror);
      const pref = { x: inst.ox + ax0, z: inst.oz + az0 };
      let cell = -1;
      if (a.spec.reserve) {
        if (!inst.reserved.has(a.index)) continue; // the reservation did not hold at seating (an optional point)
        cell = nearestFree(b, cells, pref.x, pref.z, 350, fl.dist);
      } else {
        // the preferred side, then the piece's other sides, then anything in reach
        const sides: Side[] = [a.spec.side, ...meta.approach.filter((s) => s !== a.spec.side)];
        for (const s of sides) {
          const n = sideNormal(s);
          const turn = member.q; // the member's own quarter turn in the floor
          const [nx2, nz2] = xf(n.nx, n.nz, turn, false);
          const half = Math.abs(nx2) > 0 ? member.hx : member.hz;
          const px = member.x + nx2 * (half + mmOf(a.spec.dist ?? 0.45)), pz = member.z + nz2 * (half + mmOf(a.spec.dist ?? 0.45));
          const idx = nearestFree(b, cells, px, pz, 150, fl.dist);
          if (idx >= 0) { cell = idx; break; }
        }
        if (cell < 0) cell = nearestFree(b, cells, member.x, member.z, Math.round(meta.reach * 1000), fl.dist);
      }
      const zone = inst.zone;
      if (cell < 0) { out.failed.push({ inst: inst.id, member, required: !!a.spec.required, kind: a.spec.kind, zone }); continue; }
      const at = a.spec.reserve ? pref : centreOf(cell);
      const kind = a.spec.kind;
      const ck = `${zone}/${inst.kit.spec.id}/${kind}`;
      const n = counter.get(ck) ?? 0; counter.set(ck, n + 1);
      let facing = Math.atan2(member.x - at.x, member.z - at.z);
      if (a.spec.faceOut) facing += Math.PI;
      const odd = (inst.q & 1) === 1;
      const zs = c.zones.get(zone);
      const anchor: Resolved['anchors'][number] = {
        id: `${ck}#${n}`, zone, floor: b.f, kind, x: member.x / 1000, z: member.z / 1000, ax: at.x / 1000, az: at.z / 1000,
        facing: quantYaw(facing), envelope: { hx: (odd ? a.ehz : a.ehx) / 1000, hz: (odd ? a.ehx : a.ehz) / 1000 }, piece: '',
        access: a.spec.access ?? zs?.access ?? 'public', routeM: (fl.dist[cell] * CELL_MM) / 1000,
        inst: inst.id, member, required: !!a.spec.required, cell,
      };
      if (a.spec.socket !== undefined && meta.sockets.length > 0) {
        const s = meta.sockets[Math.min(a.spec.socket, meta.sockets.length - 1)];
        const [sx, sz] = xf(mmOf(s.dx), 0, member.q, false);
        anchor.seat = { pose: s.pose, eye: s.eye, x: (member.x + sx) / 1000, z: (member.z + sz) / 1000, yaw: member.yaw };
        anchor.x = anchor.seat.x; anchor.z = anchor.seat.z;
      }
      out.anchors.push(anchor);
    }
  }
  return out;
}
/** A facing in 1e-4 rad, wrapped to (−π, π] — an integer-friendly float. */
function quantYaw(y: number): number {
  let v = Math.round(y * 10000) / 10000;
  if (v > Math.PI) v -= 2 * Math.PI; else if (v <= -Math.PI) v += 2 * Math.PI;
  return Math.round(v * 10000) / 10000;
}

function zonePresent(z: ZoneSpec, r: Resolved): boolean {
  for (const kind of z.requires) if (!r.anchors.some((a) => a.zone === z.id && a.kind === kind)) return false;
  return true;
}
function removeInst(c: Cand, id: number): void {
  const inst = c.insts.find((i) => i.id === id);
  if (!inst) return;
  c.placed = c.placed.filter((p) => p.inst !== id);
  c.insts = c.insts.filter((i) => i.id !== id);
}
/** Remove one member (and whatever stands on it or needs it). */
function removeMember(c: Cand, m: Placed): void {
  const inst = c.insts.find((i) => i.id === m.inst);
  if (!inst) return;
  const gone = new Set<string>([m.role]);
  let grew = true;
  while (grew) {
    grew = false;
    for (const [role, p] of inst.seated) if (!gone.has(role) && p.needRoles.some((r) => gone.has(r))) { gone.add(role); grew = true; }
  }
  // a kit that loses a member it cannot do without is no kit: the whole instance goes
  if (!m.optional) { c.placed = c.placed.filter((q) => q.inst !== inst.id); c.insts = c.insts.filter((i) => i !== inst); return; }
  for (const role of gone) { const p = inst.seated.get(role); if (p) { c.placed = c.placed.filter((q) => q !== p); inst.seated.delete(role); } }
  if (inst.seated.size === 0) c.insts = c.insts.filter((i) => i !== inst);
}

/** Prove the candidate: every node and every mandatory zone reachable; repair ≤ R times. */
function prove(ctx: Ctx, c: Cand): { ok: boolean; res: Resolved } {
  let res = resolveAnchors(ctx, c);
  for (let round = 0; ; round++) {
    // 1. a piece whose every use failed is clutter: take it out (it only frees ground)
    let changed = false;
    const failedByMember = new Map<Placed, number>();
    for (const fa of res.failed) failedByMember.set(fa.member, (failedByMember.get(fa.member) ?? 0) + 1);
    for (const [m] of failedByMember) {
      if (!c.placed.includes(m) || res.anchors.some((a) => a.member === m)) continue; // gone already, or another of its uses holds
      const meta = pieceMeta(m.key);
      if (!(meta?.use === 'sit' || meta?.use === 'lie' || meta?.use === 'storage')) continue;
      // its own optional kit-mates may be what stands in the way (a side table in the only approach): they go first
      const inst = c.insts.find((i) => i.id === m.inst);
      const mates = inst ? [...inst.seated.values()].filter((p) => p !== m && p.optional && p.blocksWalk && !m.needRoles.includes(p.role)).reverse() : [];
      let saved = false;
      const keepPlaced = c.placed, keepSeated = inst ? new Map(inst.seated) : null;
      for (const mate of mates) {
        removeMember(c, mate);
        res = resolveAnchors(ctx, c);
        if (res.anchors.some((a) => a.member === m)) { saved = true; c.reasons.push(`made-way:${mate.zone}/${mate.kit}/${mate.role}`); break; }
      }
      if (saved) continue;
      // nothing helped: the mates come back before the piece itself leaves
      if (inst && keepSeated) { c.placed = keepPlaced; inst.seated.clear(); for (const [k, v] of keepSeated) inst.seated.set(k, v); }
      // AN ACCESSORY YIELDS BEFORE A SEAT DOES: another kit's optional member within reach (a light column by a bench's
      // open end) goes first. A whole kit never yields to one seat — a fourth bay is not worth the only stage.
      const mb = boxOf(m);
      const near = (p: Placed): boolean => p.blocksWalk && gapBetween(boxOf(p), mb) < 1300;
      const accessories = c.placed.filter((p) => p.inst !== m.inst && p.inst >= 0 && p.optional && near(p) && !c.placed.some((q) => q.inst === p.inst && q.needRoles.includes(p.role)));
      for (const acc of accessories) {
        const owner = c.insts.find((i) => i.id === acc.inst);
        if (!owner) continue;
        const wasPlaced = c.placed, wasSeated = new Map(owner.seated);
        removeMember(c, acc);
        res = resolveAnchors(ctx, c);
        if (res.anchors.some((a) => a.member === m)) { saved = true; c.reasons.push(`made-way:${acc.zone}/${acc.kit}/${acc.role}`); break; }
        c.placed = wasPlaced; owner.seated.clear(); for (const [k, v] of wasSeated) owner.seated.set(k, v);
      }
      if (saved) continue;
      removeMember(c, m); c.reasons.push(`unreachable:${m.zone}/${m.kit}/${m.role}@${m.x},${m.z},q${m.q}`); changed = true;
    }
    if (changed) { res = resolveAnchors(ctx, c); }
    const missingZone = [...c.zones.values()].filter((z) => z.mandatory && !zonePresent(z, res));
    const missingNode = c.base.nodes.filter((n) => n.required && !res.nodeCells.has(n.id));
    const failedRequired = res.failed.filter((x) => x.required);
    if (missingZone.length === 0 && missingNode.length === 0 && failedRequired.length === 0) return { ok: true, res };
    if (round >= ctx.R) {
      for (const z of missingZone) c.reasons.push(`zone:missing:${z.id}`);
      for (const n of missingNode) c.reasons.push(`route:unreachable:${n.id}`);
      for (const x of failedRequired) c.reasons.push(`anchor:unreachable:${x.zone}/${x.kind}`);
      return { ok: false, res };
    }
    // 2. REPAIR: a kit whose required anchor failed goes; else the lowest-value optional zone still seated goes
    c.repairs++; ctx.work.repairs++;
    if (failedRequired.length > 0) {
      const inst = failedRequired[0].inst;
      const kit = c.insts.find((i) => i.id === inst);
      c.reasons.push(`repair:removed:${kit?.zone}/${kit?.kit.spec.id}`);
      removeInst(c, inst);
    } else {
      const optional = [...c.zones.values()].filter((z) => !z.mandatory && c.insts.some((i) => i.zone === z.id)).sort((a, b2) => (b2.dropOrder ?? 0) - (a.dropOrder ?? 0) || b2.priority - a.priority);
      if (optional.length === 0) {
        for (const z of missingZone) c.reasons.push(`zone:missing:${z.id}`);
        for (const n of missingNode) c.reasons.push(`route:unreachable:${n.id}`);
        return { ok: false, res };
      }
      const z = optional[0];
      c.reasons.push(`repair:dropped-zone:${z.id}`);
      for (const i of c.insts.filter((k) => k.zone === z.id)) removeInst(c, i.id);
    }
    res = resolveAnchors(ctx, c);
  }
}

function pathCells(fl: Flood, from: number): number[] {
  const out: number[] = [];
  for (let c = from, guard = 0; c >= 0 && guard < 400000; c = fl.parent[c], guard++) out.push(c);
  return out;
}
// ─── The soft score (integers; the primary pass only) ───────────────────────────────────────────────────────────────

function zoneBoxes(c: Cand, zoneId: string): Bx[] {
  const cut = c.zoneRects.get(zoneId);
  if (cut && cut.length > 0) return cut;
  let b: Bx | null = null;
  for (const p of c.placed) {
    if (p.zone !== zoneId) continue;
    const q = boxOf(p);
    b = b ? { x0: Math.min(b.x0, q.x0), x1: Math.max(b.x1, q.x1), z0: Math.min(b.z0, q.z0), z1: Math.max(b.z1, q.z1) } : q;
  }
  return b ? [b] : [];
}
function minGap(a: Bx[], b: Bx[]): number {
  let g = Infinity;
  for (const p of a) for (const q of b) g = Math.min(g, gapBetween(p, q));
  return g;
}
function sightClear(c: Cand, x0: number, z0: number, x1: number, z1: number): boolean {
  const steps = Math.max(1, Math.ceil(Math.hypot(x1 - x0, z1 - z0) / 100));
  for (let s = 1; s < steps; s++) {
    const x = x0 + ((x1 - x0) * s) / steps, z = z0 + ((z1 - z0) * s) / steps;
    for (const p of c.placed) {
      if (!pieceMeta(p.key)?.blocksSight) continue;
      if (x > p.x - p.hx && x < p.x + p.hx && z > p.z - p.hz && z < p.z + p.hz) return false;
    }
  }
  return true;
}
function score(ctx: Ctx, c: Cand, res: Resolved, candidateId: string): { total: number; parts: Record<string, number> } {
  const b = c.base;
  const parts: Record<string, number> = {};
  const present = [...c.zones.values()].filter((z) => c.insts.some((i) => i.zone === z.id));
  parts.zones = present.filter((z) => !z.mandatory).length * 60;
  parts.pieces = Math.min(140, c.placed.length * 2);
  const seats = res.anchors.filter((a) => a.seat);
  parts.capacity = Math.min(80, seats.length * 4);
  let adj = 0;
  for (const z of present) {
    for (const o of z.adjacentTo) { const oz = c.zones.get(o); if (!oz || !present.includes(oz)) continue; if (minGap(zoneBoxes(c, z.id), zoneBoxes(c, o)) <= 2500) adj += 25; }
    for (const o of z.separatedFrom) {
      const ob = o === 'arrival' ? [{ x0: -1200, x1: 1200, z0: -b.hd, z1: -b.hd + b.doorDepth }] : zoneBoxes(c, o);
      if (ob.length > 0 && minGap(zoneBoxes(c, z.id), ob) >= 1500) adj += 20;
    }
  }
  parts.adjacency = adj;
  const req = res.anchors.filter((a) => a.required);
  parts.routes = req.length > 0 ? -Math.round((req.reduce((s, a) => s + a.routeM, 0) / req.length) * 3) : 0;
  const cust = res.anchors.find((a) => a.kind === 'service-customer');
  parts.queueSight = cust && sightClear(c, b.entry.x, b.entry.z, mmOf(cust.ax), mmOf(cust.az)) ? 30 : 0;
  const panes = b.spec.panes.filter((p) => Math.abs(mmOf(p.y) - (b.y0 + 1850)) < 600);
  let near = 0;
  for (const a of seats) if (panes.some((p) => Math.hypot(p.x - a.ax, p.z - a.az) <= 3.5)) near++;
  parts.windows = seats.length > 0 ? Math.round((30 * near) / seats.length) : 0;
  const priv = res.anchors.filter((a) => a.access !== 'public'), pub = res.anchors.filter((a) => a.access === 'public');
  const avg = (l: typeof priv): number => (l.length > 0 ? l.reduce((s, a) => s + a.routeM, 0) / l.length : 0);
  parts.privacy = priv.length > 0 && pub.length > 0 && avg(priv) >= avg(pub) ? 25 : 0;
  let facing = 0;
  for (const a of seats) {
    const fx = Math.sin(a.seat!.yaw), fz = Math.cos(a.seat!.yaw);
    const sx = mmOf(a.seat!.x), sz = mmOf(a.seat!.z);
    let hit = false;
    for (const p of c.placed) {
      if (p === a.member || !p.blocksWalk) continue;
      const dx = p.x - sx, dz = p.z - sz;
      const along = dx * fx + dz * fz;
      if (along > 200 && along < 3200 && Math.abs(dx * fz - dz * fx) < Math.max(p.hx, p.hz) + 300) { hit = true; break; }
    }
    if (hit) facing++;
  }
  parts.facing = seats.length > 0 ? Math.round((20 * facing) / seats.length) : 0;
  let mass = 0, mx = 0;
  for (const p of c.placed) if (p.blocksWalk) { const m = (p.hx * p.hz) / 1e4; mass += m; mx += m * p.x; }
  const off = mass > 0 ? Math.abs(mx / mass) / Math.max(1, b.hw) : 0;
  parts.asymmetry = off >= 0.04 && off <= 0.45 ? 15 : 0;
  parts.repairs = -c.repairs * 20;
  parts.seed = rngOf(seedOf(ctx.input.buildingId, ctx.seedNs + (ctx.salt.topology ?? ''), b.f, candidateId, 'topology')).int(13);
  let total = 0;
  for (const k of Object.keys(parts)) total += parts[k];
  return { total, parts };
}

// ─── A floor from a program ─────────────────────────────────────────────────────────────────────────────────────────

function newCand(base: FloorBase, program: FloorProgram, arrangement: Arrangement, turn = 0): Cand {
  return {
    turn,
    base, program, arrangement, placed: [], insts: [], keeps: [], zones: new Map(program.zones.map((z) => [z.id, z])), zoneRects: new Map(), dropped: [],
    cursors: new Map(), regions: { ...base.regions }, reasons: [], units: [], repairs: 0, walls: [], wallBoxes: [], enclosed: [], divided: false,
  };
}

/** UNITS (slice R): cut the east side of the floor into rooms, a corridor on the stairs' side. */
function cutUnits(c: Cand, up: UnitPlan, lift: ReturnType<typeof liftCandidateFor>, rooms = false, storeys = false): void {
  const b = c.base;
  const main = b.regions.room;
  if (!main) return;
  const corridor = mmOf(up.corridor);
  let x0 = Math.max(main.x0 + corridor, b.hw - mmOf(up.maxWidth));
  let z1 = b.hd;
  // the lift's shaft and apron stay in the circulation: units begin east of it, or end before it
  const x1 = b.hw, z0 = -b.hd;
  const unitsIn = (zz: number): number => {
    const dp = zz - z0;
    let k = Math.min(up.maxUnits, Math.floor(dp / mmOf(up.depth.min)));
    if (k >= 1 && dp / k > mmOf(up.depth.max)) k = Math.min(up.maxUnits, Math.ceil(dp / mmOf(up.depth.max)));
    return k;
  };
  if (lift && lift.box.x1 > x0 && lift.wall === 'north') {
    if (lift.box.x1 + 100 <= b.hw - 3600) x0 = Math.max(x0, lift.box.x1 + 100);
    else {
      z1 = Math.min(z1, lift.apron.z0 - 100);
      // (slice U, the storeys revision) THE LIFT'S LOBBY: the units' closing wall stood 0.1 m off the apron — a 1.2 m
      // lobby before the lift's door. It stands `LIFT_LOBBY_MM` off the door now, WHEREVER THAT COSTS NO UNIT: the
      // same number of units in the same order, so every unit keeps its id (rentals key on them).
      const zl = lift.box.z0 - LIFT_LOBBY_MM;
      if (storeys && zl < z1 && unitsIn(zl) === unitsIn(z1) && unitsIn(zl) >= up.minUnits) z1 = zl;
    }
  }
  const depth = z1 - z0, width = x1 - x0;
  if (width < 3400) return;
  const n = unitsIn(z1);
  if (n < up.minUnits) return;
  const pitch = Math.floor(depth / n / 10) * 10;
  for (let i = 0; i < n; i++) {
    const rect: Bx = { x0, x1, z0: z0 + i * pitch, z1: i === n - 1 ? z1 : z0 + (i + 1) * pitch };
    const zid = `unit${i}`;
    c.units.push({ index: i, rect, zone: zid });
    c.zones.set(zid, { ...up.zone, id: zid, priority: i });
    c.zoneRects.set(zid, [rect]);
  }
  if (!rooms) return;
  // (slice G) EVERY UNIT IS A ROOM: one wall along the corridor (its west face on the units' line, so the corridor keeps
  // its width) with ONE doorway per unit at the unit's middle — where the threshold anchor stands — and a wall between
  // neighbours. The unit's furniture is seated inside the walls (`inner`), never under them.
  const T = PARTITION_MM, h = T / 2;
  const closing = z1 < b.hd;
  const corridorWall: Wall = { name: 'corridor', axis: 'z', at: x0 + h, lo: z0, hi: z1, height: b.headroom, screen: false, doors: [], zone: '' };
  for (const u of c.units) {
    const mid = snap((u.rect.z0 + u.rect.z1) / 2);
    const door = { lo: mid - DOORWAY_MM / 2, hi: mid + DOORWAY_MM / 2 };
    corridorWall.doors.push(door);
    for (const a of doorAprons(corridorWall, door)) c.keeps.push(a);
    u.inner = { x0: x0 + T, x1, z0: u.rect.z0 + (u.index > 0 ? h : 0), z1: u.rect.z1 - (u.index < n - 1 ? h : closing ? T : 0) };
    c.enclosed.push({ name: u.zone, x: (u.inner.x0 + u.inner.x1) / 2, z: (u.inner.z0 + u.inner.z1) / 2, access: up.zone.access });
  }
  c.walls.push(corridorWall);
  for (let i = 1; i < n; i++) c.walls.push({ name: `unit${i}`, axis: 'x', at: z0 + i * pitch, lo: x0, hi: x1, height: b.headroom, screen: false, doors: [], zone: `unit${i}` });
  if (closing) c.walls.push({ name: 'units-end', axis: 'x', at: z1 - h, lo: x0, hi: x1, height: b.headroom, screen: false, doors: [], zone: '' });
  syncWalls(c);
}

// ─── Partitions (slice G) ────────────────────────────────────────────────────────────────────────────────────────────

function wallBox(w: Pick<Wall, 'axis' | 'at'>, lo: number, hi: number): Bx {
  const h = PARTITION_MM / 2;
  return w.axis === 'x' ? { x0: lo, x1: hi, z0: w.at - h, z1: w.at + h } : { x0: w.at - h, x1: w.at + h, z0: lo, z1: hi };
}
/** The wall less its doorways. */
function wallSolids(w: Wall): Bx[] {
  const out: Bx[] = [];
  let from = w.lo;
  for (const d of [...w.doors].sort((a, b) => a.lo - b.lo)) { if (d.lo > from) out.push(wallBox(w, from, d.lo)); from = Math.max(from, d.hi); }
  if (w.hi > from) out.push(wallBox(w, from, w.hi));
  return out;
}
/** The ground kept clear either side of a doorway: [the low side (−z / −x), the high side]. */
function doorAprons(w: Pick<Wall, 'axis' | 'at'>, d: { lo: number; hi: number }): [Bx, Bx] {
  const h = PARTITION_MM / 2;
  return w.axis === 'x'
    ? [{ x0: d.lo, x1: d.hi, z0: w.at - h - DOOR_APRON_MM, z1: w.at - h }, { x0: d.lo, x1: d.hi, z0: w.at + h, z1: w.at + h + DOOR_APRON_MM }]
    : [{ x0: w.at - h - DOOR_APRON_MM, x1: w.at - h, z0: d.lo, z1: d.hi }, { x0: w.at + h, x1: w.at + h + DOOR_APRON_MM, z0: d.lo, z1: d.hi }];
}
function syncWalls(c: Cand): void { c.wallBoxes = c.walls.flatMap(wallSolids); }
const mid2 = (q: Bx): { x: number; z: number } => ({ x: (q.x0 + q.x1) / 2, z: (q.z0 + q.z1) / 2 });
const reservedBx = (r: ReservedRegion): Bx => ({ x0: mmOf(r.rect.x0), x1: mmOf(r.rect.x1), z0: mmOf(r.rect.z0), z1: mmOf(r.rect.z1) });

/** May a wall stand in `box`? Never in a shell solid, on walked or kept ground, in the door corridor, the stair zone,
 *  the landing or the lift, and never through a piece (a rug included — a wall on a rug reads as a mistake). */
function wallClear(c: Cand, box: Bx): boolean {
  const b = c.base;
  if (box.x0 < -b.hw || box.x1 > b.hw || box.z0 < -b.hd || box.z1 > b.hd) return false;
  for (const s of b.solids) if (meets(box, s)) return false;
  for (const s of b.walked) if (meets(box, s)) return false;
  for (const k of b.keep) if (meets(box, k)) return false;
  for (const k of c.keeps) if (meets(box, k)) return false;
  for (const r of b.reserved) if ((r.kind === 'door' || r.kind === 'stair' || r.kind === 'landing' || r.kind === 'lift') && meets(box, reservedBx(r))) return false;
  for (const p of c.placed) { const q = boxOf(p), g = p.blocksWalk ? 50 : 0; if (meets(box, { x0: q.x0 - g, x1: q.x1 + g, z0: q.z0 - g, z1: q.z1 + g })) return false; }
  for (const o of b.owned) if (meets(box, boxOf(o))) return false;
  return true;
}
/** Is a doorway's apron free ground (no solid, no blocking piece, on the floor)? */
function apronClear(c: Cand, box: Bx): boolean {
  const b = c.base;
  if (box.x0 < -b.hw + 100 || box.x1 > b.hw - 100 || box.z0 < -b.hd + 100 || box.z1 > b.hd - 100) return false;
  for (const s of b.solids) if (meets(box, s)) return false;
  for (const s of c.wallBoxes) if (meets(box, s)) return false;
  for (const p of c.placed) if (p.blocksWalk && meets(box, boxOf(p))) return false;
  for (const o of b.owned) if (o.blocksWalk && meets(box, boxOf(o))) return false;
  return true;
}
/** The best doorway of `width` on a wall within [lo, hi]: both aprons free and both standing points WALKED today
 *  (reached by the route with everything seated); the lowest `prefer` wins, ties the lower coordinate. */
function pickDoor(c: Cand, res: Resolved, w: Wall, lo: number, hi: number, width: number, prefer: (p: number, dist: number) => number): { lo: number; hi: number } | null {
  const b = c.base;
  let best: { lo: number; hi: number } | null = null, bestKey = Infinity;
  for (let p = Math.ceil((lo + JAMB_MM + width / 2) / 100) * 100; p + width / 2 + JAMB_MM <= hi; p += 100) {
    const d = { lo: p - width / 2, hi: p + width / 2 };
    const [a1, a2] = doorAprons(w, d);
    if (!apronClear(c, a1) || !apronClear(c, a2)) continue;
    const pa = mid2(a1), pb = mid2(a2);
    const ca = nearestFree(b, res.cells, pa.x, pa.z, 300, res.fl.dist), cb = nearestFree(b, res.cells, pb.x, pb.z, 300, res.fl.dist);
    if (ca < 0 || cb < 0) continue;
    const key = prefer(p, Math.min(res.fl.dist[ca], res.fl.dist[cb]));
    if (key < bestKey) { bestKey = key; best = d; }
  }
  return best;
}
/** Everything proven before still holds: every anchor on its own member, every node reached. */
function holds(before: Resolved, after: Resolved): boolean {
  for (const a of before.anchors) if (!after.anchors.some((x) => x.id === a.id && x.member === a.member)) return false;
  for (const [id] of before.nodeCells) if (!after.nodeCells.has(id)) return false;
  return true;
}
/** Is every doorway of the candidate reached on BOTH sides by the route (every wall and every piece solid)? */
function doorsReached(c: Cand, res: Resolved): boolean {
  for (const w of c.walls) for (const d of w.doors) for (const a of doorAprons(w, d)) {
    const m = mid2(a);
    if (nearestFree(c.base, res.cells, m.x, m.z, 300, res.fl.dist) < 0) return false;
  }
  return true;
}
/** Add walls (with their door aprons kept) and re-prove; undo and return null unless everything proven before still
 *  holds and both sides of every new doorway are reached. THE WITHDRAWN-NOT-NEGOTIATED LAW: a wall never moves a piece. */
function commitWalls(ctx: Ctx, c: Cand, res: Resolved, walls: Wall[]): Resolved | null {
  const nW = c.walls.length, nK = c.keeps.length;
  c.walls.push(...walls);
  for (const w of walls) for (const d of w.doors) c.keeps.push(...doorAprons(w, d));
  syncWalls(c);
  const next = resolveAnchors(ctx, c);
  if (holds(res, next) && doorsReached(c, next)) return next;
  c.walls.length = nW; c.keeps.length = nK; syncWalls(c);
  return null;
}

/** The ground a seated zone takes: its blocking pieces and the standing points of its anchors (mm), or null. */
function zoneGround(c: Cand, res: Resolved, zoneId: string): Bx | null {
  let bb: Bx | null = null;
  const add = (q: Bx): void => { bb = bb ? { x0: Math.min(bb.x0, q.x0), x1: Math.max(bb.x1, q.x1), z0: Math.min(bb.z0, q.z0), z1: Math.max(bb.z1, q.z1) } : q; };
  for (const p of c.placed) if (p.zone === zoneId && p.blocksWalk) add(boxOf(p));
  for (const a of res.anchors) if (a.zone === zoneId) add({ x0: mmOf(a.ax) - 400, x1: mmOf(a.ax) + 400, z0: mmOf(a.az) - 400, z1: mmOf(a.az) + 400 });
  return bb;
}
const down100 = (v: number): number => Math.floor(v / 100) * 100, up100 = (v: number): number => Math.ceil(v / 100) * 100;

/** THE FORECOURT (slice U): how far the route's free cells run straight out from a doorway's OUTER standing point (mm,
 *  up to 2.4 m) — the outer side is the apron that is not inside the room `R`. */
function forecourt(c: Cand, res: Resolved, w: Wall, d: { lo: number; hi: number }, R: Bx): number {
  const [a1, a2] = doorAprons(w, d);
  const m1 = mid2(a1), m2 = mid2(a2), ctr = mid2(R);
  // the inner side is the apron nearer the room's own centre
  const in1 = Math.hypot(m1.x - ctr.x, m1.z - ctr.z) <= Math.hypot(m2.x - ctr.x, m2.z - ctr.z);
  const out = in1 ? m2 : m1, inn = in1 ? m1 : m2;
  const dx = w.axis === 'z' ? Math.sign(out.x - inn.x) : 0, dz = w.axis === 'x' ? Math.sign(out.z - inn.z) : 0;
  // from the free cell the doorway's outer standing point resolves to (the point itself is within a body of the wall)
  const b = c.base;
  const start = nearestFree(b, res.cells, out.x, out.z, 300, res.fl.dist);
  if (start < 0) return 0;
  const i0 = start % b.nx, j0 = (start - i0) / b.nx;
  let run = 0;
  for (let n = 1; n * CELL_MM <= 2400; n++) {
    const i = i0 + dx * n, j = j0 + dz * n;
    if (i < 0 || j < 0 || i >= b.nx || j >= b.nz || res.cells[j * b.nx + i]) break;
    run = n * CELL_MM;
  }
  return run;
}
/**
 * (slice U, the storeys revision) A ROOM'S DOORWAY IS RE-OPENED WHERE THE FLOOR IS OPEN. A room's one doorway is cut
 * when the room is enclosed; a room enclosed AFTER it may stand its wall 0.8 m before that doorway (the large house:
 * the main bedroom's door opened off the slot between it and the spare room). Once every room stands, a doorway whose
 * forecourt is shorter than `FORECOURT_MM` moves to the place on its own room's walls that the route reaches first
 * among those with a forecourt — and only if everything proven still holds with it there (else it stays).
 */
function reopenDoors(ctx: Ctx, c: Cand, res: Resolved): Resolved {
  for (const room of c.enclosed) {
    const walls = c.walls.filter((w) => w.zone === room.name && !w.screen && w.name.startsWith(`${room.name}:`));
    const holder = walls.find((w) => w.doors.length === 1);
    if (!holder || walls.reduce((n, w) => n + w.doors.length, 0) !== 1) continue;
    const R: Bx = { x0: room.x - 1, x1: room.x + 1, z0: room.z - 1, z1: room.z + 1 };
    const old = holder.doors[0];
    if (forecourt(c, res, holder, old, R) >= FORECOURT_MM) continue;
    let pick: { w: Wall; d: { lo: number; hi: number }; key: number } | null = null;
    for (const w of walls) {
      let key = Infinity;
      const d = pickDoor(c, res, w, w.lo + PARTITION_MM, w.hi - PARTITION_MM, DOORWAY_MM, (p, dist) => {
        const dd = { lo: p - DOORWAY_MM / 2, hi: p + DOORWAY_MM / 2 };
        if (w === holder && dd.lo < old.hi && dd.hi > old.lo) return Infinity; // the doorway it has, or one overlapping it
        const k = forecourt(c, res, w, dd, R) >= FORECOURT_MM ? dist : Infinity;
        key = Math.min(key, k); return k;
      });
      if (d && key < Infinity && (!pick || key < pick.key)) pick = { w, d, key };
    }
    if (!pick) continue;
    // swap: the old aperture closes (its aprons are no longer kept), the new one opens
    const keepsBefore = [...c.keeps];
    const oldAprons = doorAprons(holder, old);
    c.keeps = c.keeps.filter((k) => !oldAprons.some((a) => a.x0 === k.x0 && a.x1 === k.x1 && a.z0 === k.z0 && a.z1 === k.z1));
    holder.doors.length = 0;
    pick.w.doors.push(pick.d);
    c.keeps.push(...doorAprons(pick.w, pick.d));
    syncWalls(c);
    const next = resolveAnchors(ctx, c);
    if (holds(res, next) && doorsReached(c, next)) { res = next; c.reasons.push(`room:door-moved:${room.name}`); continue; }
    pick.w.doors.length = 0; holder.doors.push(old); c.keeps = keepsBefore; syncWalls(c);
  }
  return res;
}
/** How many of a candidate's ROOM doorways open off a slot (no forecourt) — after its rooms are enclosed. */
function slotDoors(ev: Evaluated): number {
  const c = ev.c;
  let n = 0;
  for (const room of c.enclosed) {
    const R: Bx = { x0: room.x - 1, x1: room.x + 1, z0: room.z - 1, z1: room.z + 1 };
    for (const w of c.walls) if (w.zone === room.name && !w.screen && w.name.startsWith(`${room.name}:`)) for (const d of w.doors) if (forecourt(c, ev.res, w, d, R) < FORECOURT_MM) n++;
  }
  return n;
}
/** ENCLOSE A ZONE the program asks a room for: walls on the sides of its ground that are not the shell (a side within
 *  1.5 m of the shell takes the shell), pushed out until they stand clear, ONE doorway where the route comes nearest. */
function encloseZone(ctx: Ctx, c: Cand, res: Resolved, z: ZoneSpec): Resolved | null {
  const b = c.base, T = PARTITION_MM, h = T / 2;
  const bb = zoneGround(c, res, z.id);
  if (!bb) return null;
  for (let pad = 300; pad <= 1300; pad += 100) {
    const R: Bx = { x0: down100(bb.x0 - pad), x1: up100(bb.x1 + pad), z0: down100(bb.z0 - pad), z1: up100(bb.z1 + pad) };
    const sh = { W: R.x0 - T < -b.hw + 1500, E: R.x1 + T > b.hw - 1500, S: R.z0 - T < -b.hd + 1500, N: R.z1 + T > b.hd - 1500 };
    if (sh.W) R.x0 = -b.hw; if (sh.E) R.x1 = b.hw; if (sh.S) R.z0 = -b.hd; if (sh.N) R.z1 = b.hd;
    if (R.x1 - R.x0 < 2400 || R.z1 - R.z0 < 2400) continue;
    if ((R.x1 - R.x0) * (R.z1 - R.z0) > 0.7 * 4 * b.hw * b.hd) return null; // most of the floor is not a room in it
    const zl = R.z0 - (sh.S ? 0 : T), zh = R.z1 + (sh.N ? 0 : T), xl = R.x0 - (sh.W ? 0 : T), xh = R.x1 + (sh.E ? 0 : T);
    const mk = (side: string, axis: 'x' | 'z', at: number, lo: number, hi: number): Wall => ({ name: `${z.id}:${side}`, axis, at, lo, hi, height: b.headroom, screen: false, doors: [], zone: z.id });
    const walls: Wall[] = [];
    if (!sh.W) walls.push(mk('W', 'z', R.x0 - h, zl, zh));
    if (!sh.E) walls.push(mk('E', 'z', R.x1 + h, zl, zh));
    if (!sh.S) walls.push(mk('S', 'x', R.z0 - h, xl, xh));
    if (!sh.N) walls.push(mk('N', 'x', R.z1 + h, xl, xh));
    if (walls.length === 0) return null;
    if (!walls.every((w) => wallClear(c, wallBox(w, w.lo, w.hi)))) continue;
    // one doorway: on the wall the route reaches first — and, from the storeys revision on, one that opens onto open
    // floor (THE FORECOURT): a doorway off the 0.8 m slot between two rooms is taken only when there is no other
    let pick: { w: Wall; d: { lo: number; hi: number }; key: number } | null = null;
    for (const w of walls) {
      let key = Infinity;
      const d = pickDoor(c, res, w, w.axis === 'x' ? R.x0 : R.z0, w.axis === 'x' ? R.x1 : R.z1, DOORWAY_MM, (p, dist) => {
        const k = ctx.storeys && forecourt(c, res, w, { lo: p - DOORWAY_MM / 2, hi: p + DOORWAY_MM / 2 }, R) < FORECOURT_MM ? dist + 1_000_000 : dist;
        key = Math.min(key, k); return k;
      });
      if (d && (!pick || key < pick.key)) pick = { w, d, key };
    }
    if (!pick) continue;
    pick.w.doors.push(pick.d);
    const next = commitWalls(ctx, c, res, walls);
    if (!next) { pick.w.doors.length = 0; continue; }
    c.enclosed.push({ name: z.id, x: (R.x0 + R.x1) / 2, z: (R.z0 + R.z1) / 2, access: z.access });
    return next;
  }
  return null;
}

/** A SCREEN for a zone that asks for one: a low partition on the side of its ground that faces the floor's entry,
 *  covering most of that side and leaving the end nearer the entry open — it never closes anything. */
function screenZone(ctx: Ctx, c: Cand, res: Resolved, z: ZoneSpec): Resolved | null {
  const b = c.base, T = PARTITION_MM, h = T / 2;
  const bb = zoneGround(c, res, z.id);
  if (!bb) return null;
  const cx = (bb.x0 + bb.x1) / 2, cz = (bb.z0 + bb.z1) / 2;
  const ex = b.entry.x - cx, ez = b.entry.z - cz;
  // the side whose outward normal looks most at the entry, the shell's own sides excepted
  const sides: Array<{ side: string; axis: 'x' | 'z'; dot: number }> = [
    { side: 'W', axis: 'z' as const, dot: -ex }, { side: 'E', axis: 'z' as const, dot: ex }, { side: 'S', axis: 'x' as const, dot: -ez }, { side: 'N', axis: 'x' as const, dot: ez },
  ].filter((q) => (q.side === 'W' ? bb.x0 > -b.hw + 1500 : q.side === 'E' ? bb.x1 < b.hw - 1500 : q.side === 'S' ? bb.z0 > -b.hd + 1500 : bb.z1 < b.hd - 1500)).sort((p, q) => q.dot - p.dot);
  for (const s of sides.slice(0, 1)) {
    const span0 = s.axis === 'x' ? bb.x0 : bb.z0, span1 = s.axis === 'x' ? bb.x1 : bb.z1, S = span1 - span0;
    const len = down100(Math.min(S, Math.max(1600, 0.7 * S)));
    if (len < 1600) return null;
    // the open end is the one nearer the entry
    const entryAlong = s.axis === 'x' ? b.entry.x : b.entry.z;
    const lo = Math.abs(entryAlong - span0) < Math.abs(entryAlong - span1) ? down100(span1 - len) : up100(span0);
    for (let off = 350; off <= 950; off += 100) {
      const at = s.side === 'W' ? down100(bb.x0 - off) - h : s.side === 'E' ? up100(bb.x1 + off) + h : s.side === 'S' ? down100(bb.z0 - off) - h : up100(bb.z1 + off) + h;
      const w: Wall = { name: `screen:${z.id}`, axis: s.axis, at, lo, hi: lo + len, height: Math.min(SCREEN_HEIGHT_MM, b.headroom), screen: true, doors: [], zone: z.id };
      if (!wallClear(c, wallBox(w, w.lo, w.hi))) continue;
      const next = commitWalls(ctx, c, res, [w]);
      if (next) return next;
    }
  }
  return null;
}

/** DIVIDE A LARGE FLOOR (slice F's verdict: big floors need partitions): full-span walls laid in the gaps the seated
 *  furniture leaves, across the floor's longer way — chambers about a pitch deep, at most CHAMBER_WALLS of them —
 *  each with one wide doorway near the floor's axis; a floor wider than two chambers also gets a wall down each side
 *  with a doorway per chamber (a nave with side rooms). Never a maze: every room opens onto the chain from the entry.
 *  The walls of a step are proven TOGETHER (one flood), then one by one if that fails — WORK, bounded. */
function divideFloor(ctx: Ctx, c: Cand, res: Resolved, pitch = CHAMBER_MM): Resolved {
  const b = c.base;
  if (c.units.length > 0 || b.usableM2 < DIVIDE_M2 || c.divided || DIVIDE_POLICY[c.program.id] === 'none') return res;
  const W = 2 * b.hw, D = 2 * b.hd, h = PARTITION_MM / 2;
  let out = res;
  let budget = 5; // re-proofs
  /** The nearest line to `ideal` where a full-span wall stands clear, with its doorways — not committed. */
  const find = (axis: 'x' | 'z', ideal: number, name: string, spans: Array<[number, number]>, doorAt: number, taken: number[]): Wall | null => {
    const span = axis === 'x' ? D : W, least = Math.max(3000, 0.2 * span);
    for (let k = 0; k <= 90; k++) { // ±4.5 m: the stair zone sits mid-depth on every multi-floor shell
      const at = Math.round((ideal + (k === 0 ? 0 : (k % 2 === 1 ? 1 : -1) * Math.ceil(k / 2) * 100)) / 100) * 100;
      // never a sliver: each side keeps a fifth of the floor's span (and 3 m), and two walls stand a room apart
      const from = at + (axis === 'x' ? b.hd : b.hw);
      if (from < least || span - from < least || taken.some((t) => Math.abs(t - at) < 3000 + PARTITION_MM)) continue;
      const w: Wall = { name, axis, at, lo: axis === 'x' ? -b.hw : -b.hd, hi: axis === 'x' ? b.hw : b.hd, height: b.headroom, screen: false, doors: [], zone: '' };
      if (!wallClear(c, wallBox(w, w.lo, w.hi))) continue;
      let ok = true;
      for (const [lo, hi] of spans) {
        const d = pickDoor(c, out, w, lo, hi, HALL_DOORWAY_MM, (p) => Math.abs(p - Math.max(lo, Math.min(hi, doorAt))));
        if (!d) { ok = false; break; }
        w.doors.push(d);
      }
      if (ok) return w;
    }
    return null;
  };
  /** Commit a step's walls together; if that does not hold, each alone. Returns the ones that stand. */
  const commit = (walls: Wall[]): Wall[] => {
    if (walls.length === 0 || budget <= 0) return [];
    budget--;
    const all = commitWalls(ctx, c, out, walls);
    if (all) { out = all; return walls; }
    const kept: Wall[] = [];
    if (walls.length > 1) for (const w of walls) {
      if (budget <= 0) break;
      budget--;
      const one = commitWalls(ctx, c, out, [w]);
      if (one) { out = one; kept.push(w); }
    }
    return kept;
  };
  if (D >= W) {
    if (D < 12000) return out;
    const n = Math.max(1, Math.min(CHAMBER_WALLS, Math.round(D / pitch) - 1));
    const found: Wall[] = [];
    for (let i = 1; i <= n; i++) {
      const w = find('x', -b.hd + (n === 1 ? 0.58 * D : (D * i) / (n + 1)), `div${i}`, [[-b.hw, b.hw]], 0, found.map((q) => q.at));
      if (w) found.push(w);
    }
    const cross = commit(found).map((w) => w.at).sort((p, q) => p - q);
    // a nave with side rooms: a wall down each side, a doorway per chamber
    if (cross.length >= 2 && W >= 30000) {
      const edges = [-b.hd, ...cross, b.hd];
      const spans: Array<[number, number]> = [];
      for (let i = 0; i + 1 < edges.length; i++) spans.push([edges[i] + (i === 0 ? 0 : h), edges[i + 1] - (i + 2 === edges.length ? 0 : h)]);
      const s1 = find('z', -b.hw + 0.25 * W, 'side1', spans, Infinity, []), s2 = find('z', -b.hw + 0.75 * W, 'side2', spans, Infinity, s1 ? [s1.at] : []);
      commit([s1, s2].filter((w): w is Wall => !!w));
    } else if (cross.length === 0 && W >= 24000) {
      // a nave no cross wall can pass (long tables down it): the side aisles become rooms instead
      const s1 = find('z', -b.hw + 0.22 * W, 'side1', [[-b.hd, b.hd]], -b.hd + 0.4 * D, []), s2 = find('z', -b.hw + 0.78 * W, 'side2', [[-b.hd, b.hd]], -b.hd + 0.4 * D, s1 ? [s1.at] : []);
      commit([s1, s2].filter((w): w is Wall => !!w));
    }
  } else {
    if (W < 12000) return out;
    const n = Math.max(1, Math.min(2, Math.round(W / pitch) - 1));
    const found: Wall[] = [];
    (n === 1 ? [0.7] : [0.28, 0.72]).forEach((fr, i) => { const w = find('z', -b.hw + fr * W, `div${i + 1}`, [[-b.hd, b.hd]], -b.hd + 0.4 * D, found.map((q) => q.at)); if (w) found.push(w); });
    commit(found);
  }
  return out;
}

/** THE ROOMS PASS over the winning candidate, after its primary pieces are proven and before it is dressed: the zones
 *  that ask for a room, then the screens, then the division of a large floor. Every wall is laid where nothing stands
 *  and is withdrawn unless every anchor and node proven before it still resolves — nothing is ever sealed. */
function enclose(ctx: Ctx, ev: Evaluated): Resolved {
  const c = ev.c;
  let res = ev.res;
  if (c.walls.length > 0) res = resolveAnchors(ctx, c); // the unit walls were proven with the candidate
  if (c.units.length > 0) return res;
  const zones = [...c.zones.values()].filter((z) => c.insts.some((i) => i.zone === z.id)).sort((a, b2) => a.priority - b2.priority || (a.id < b2.id ? -1 : 1));
  for (const z of zones) {
    if (z.enclosure !== 'room') continue;
    const next = encloseZone(ctx, c, res, z);
    if (next) res = next; else c.reasons.push(`room:open:${z.id}`);
  }
  if (ctx.storeys) res = reopenDoors(ctx, c, res);
  for (const z of zones) {
    if (z.enclosure !== 'screen') continue;
    const next = screenZone(ctx, c, res, z);
    if (next) res = next;
  }
  return divideFloor(ctx, c, res);
}

// ─── Rooms from the walls (slice G) ──────────────────────────────────────────────────────────────────────────────────

interface RoomSet { partitions: DescPartition[]; rooms: DescRoom[]; doorways: DescDoorway[] }
/** The floor's rooms: the connected regions of the floor rectangle with every NON-SCREEN wall solid and every doorway
 *  PLUGGED; a doorway joins the two regions either side of it. Furniture is not a wall — a room is geometry. */
function roomsOf(c: Cand, res: Resolved): RoomSet {
  const b = c.base, f = b.f;
  const partitions: DescPartition[] = c.walls.map((w) => {
    const x0 = w.axis === 'x' ? w.lo : w.at, z0 = w.axis === 'x' ? w.at : w.lo, x1 = w.axis === 'x' ? w.hi : w.at, z1 = w.axis === 'x' ? w.at : w.hi;
    return {
      id: `part/f${f}/${w.name}`, floor: f, x0: x0 / 1000, z0: z0 / 1000, x1: x1 / 1000, z1: z1 / 1000, thickness: PARTITION_MM / 1000, height: w.height / 1000,
      blocksWalk: true, coverHeight: w.height / 1000, blocksSight: w.height >= 1500,
      apertures: [...w.doors].sort((p, q) => p.lo - q.lo).map((d) => [(d.lo - w.lo) / 1000, (d.hi - w.lo) / 1000] as [number, number]),
    };
  });
  const whole: DescRoom = {
    id: roomIdOf(f, 'main'), floor: f, zone: 'main', access: 'public', rects: [{ x0: -b.hw / 1000, z0: -b.hd / 1000, x1: b.hw / 1000, z1: b.hd / 1000 }],
    areaM2: Math.round((4 * b.hw * b.hd) / 1e4) / 100, x: b.entry.x / 1000, z: b.entry.z / 1000,
  };
  const closing = c.walls.filter((w) => !w.screen);
  if (closing.length === 0) return { partitions, rooms: [whole], doorways: [] };
  const nx = b.nx, nz = b.nz, n = nx * nz;
  const wallCells = new Uint8Array(n);
  for (const w of closing) Raster.block(wallCells, b, wallBox(w, w.lo, w.hi), 0);
  // components, in cell-index order
  const comp = new Int32Array(n).fill(-1);
  const queue = new Int32Array(n);
  let comps = 0;
  for (let s = 0; s < n; s++) {
    if (wallCells[s] || comp[s] >= 0) continue;
    let head = 0, tail = 0;
    queue[tail++] = s; comp[s] = comps;
    while (head < tail) {
      const k = queue[head++], i = k % nx;
      if (i + 1 < nx && !wallCells[k + 1] && comp[k + 1] < 0) { comp[k + 1] = comps; queue[tail++] = k + 1; }
      if (i > 0 && !wallCells[k - 1] && comp[k - 1] < 0) { comp[k - 1] = comps; queue[tail++] = k - 1; }
      if (k + nx < n && !wallCells[k + nx] && comp[k + nx] < 0) { comp[k + nx] = comps; queue[tail++] = k + nx; }
      if (k - nx >= 0 && !wallCells[k - nx] && comp[k - nx] < 0) { comp[k - nx] = comps; queue[tail++] = k - nx; }
    }
    comps++;
  }
  if (comps <= 1) return { partitions, rooms: [whole], doorways: [] };
  const compAt = (x: number, z: number): number => { const k = Raster.index(b, x, z); return k < 0 ? -1 : comp[k]; };
  // names: the entry's region is `main`; a unit's and an enclosed zone's region takes its name; the rest are halls
  const names = new Array<string>(comps).fill(''), access = new Array<AccessRole>(comps).fill('public');
  const entryComp = compAt(b.entry.x, b.entry.z);
  if (entryComp >= 0) names[entryComp] = 'main';
  for (const e of c.enclosed) { const k = compAt(e.x, e.z); if (k >= 0 && !names[k]) { names[k] = e.name; access[k] = e.access; } }
  // per region: area, centroid, the reached cell nearest the centroid, the walking distance from the entry
  const count = new Int32Array(comps), sx = new Float64Array(comps), sz = new Float64Array(comps);
  for (let k = 0; k < n; k++) { const q = comp[k]; if (q < 0) continue; count[q]++; sx[q] += k % nx; sz[q] += (k - (k % nx)) / nx; }
  const best = new Int32Array(comps).fill(-1), bestD = new Float64Array(comps).fill(Infinity);
  for (let k = 0; k < n; k++) {
    const q = comp[k];
    if (q < 0 || res.cells[k] || res.fl.dist[k] < 0) continue;
    const i = k % nx, j = (k - i) / nx, dx = i - sx[q] / count[q], dz = j - sz[q] / count[q], d2 = dx * dx + dz * dz;
    if (d2 < bestD[q]) { bestD[q] = d2; best[q] = k; }
  }
  const unnamed = [...Array(comps).keys()].filter((q) => !names[q] && count[q] >= 100).sort((p, q) => (best[p] < 0 ? 1e9 : res.fl.dist[best[p]]) - (best[q] < 0 ? 1e9 : res.fl.dist[best[q]]) || p - q);
  unnamed.forEach((q, i) => { names[q] = `hall${i + 1}`; });
  // the rect cover: per row the runs of one region; a run continued row after row is one rect
  const rects: Rect[][] = Array.from({ length: comps }, () => []);
  let open = new Map<string, number>();
  const emit = (key: string, j0: number, j1: number): void => {
    const [q, i0, i1] = key.split(':').map(Number);
    rects[q].push({ x0: (-b.hw + i0 * CELL_MM) / 1000, z0: (-b.hd + j0 * CELL_MM) / 1000, x1: Math.min(b.hw, -b.hw + (i1 + 1) * CELL_MM) / 1000, z1: Math.min(b.hd, -b.hd + (j1 + 1) * CELL_MM) / 1000 });
  };
  for (let j = 0; j <= nz; j++) {
    const now = new Map<string, number>();
    if (j < nz) for (let i = 0; i < nx;) {
      const q = comp[j * nx + i];
      if (q < 0) { i++; continue; }
      let e = i; while (e + 1 < nx && comp[j * nx + e + 1] === q) e++;
      const key = `${q}:${i}:${e}`;
      now.set(key, open.get(key) ?? j);
      i = e + 1;
    }
    for (const [key, j0] of open) if (!now.has(key)) emit(key, j0, j - 1);
    open = now;
  }
  const rooms: DescRoom[] = [];
  const idOf = new Array<string>(comps).fill('');
  for (let q = 0; q < comps; q++) {
    if (!names[q]) continue; // a sliver between walls is nobody's room
    idOf[q] = roomIdOf(f, names[q]);
    const at = best[q] >= 0 ? { x: (-b.hw + (best[q] % nx) * CELL_MM + CELL_MM / 2) / 1000, z: (-b.hd + ((best[q] - (best[q] % nx)) / nx) * CELL_MM + CELL_MM / 2) / 1000, ok: true } : { x: (-b.hw + (sx[q] / count[q]) * CELL_MM) / 1000, z: (-b.hd + (sz[q] / count[q]) * CELL_MM) / 1000, ok: false };
    if (!at.ok) c.reasons.push(`room:sealed:${names[q]}`);
    rooms.push({ id: idOf[q], floor: f, zone: names[q], access: access[q], rects: rects[q], areaM2: Math.round(count[q]) / 100, x: at.x, z: at.z });
  }
  rooms.sort((p, q) => (p.zone === 'main' ? -1 : q.zone === 'main' ? 1 : p.id < q.id ? -1 : 1));
  const doorways: DescDoorway[] = [];
  for (const w of closing) {
    for (const d of [...w.doors].sort((p, q) => p.lo - q.lo)) {
      const [a1, a2] = doorAprons(w, d);
      const pa = mid2(a1), pb = mid2(a2);
      const qa = compAt(pa.x, pa.z), qb = compAt(pb.x, pb.z);
      if (qa < 0 || qb < 0 || qa === qb || !idOf[qa] || !idOf[qb]) continue; // the wall closes nothing here: an opening, not a doorway
      const ca = nearestFree(b, res.cells, pa.x, pa.z, 300, res.fl.dist), cb = nearestFree(b, res.cells, pb.x, pb.z, 300, res.fl.dist);
      const pt = (cell: number, fb: { x: number; z: number }): { x: number; z: number } => (cell >= 0 ? { x: -b.hw + (cell % nx) * CELL_MM + CELL_MM / 2, z: -b.hd + ((cell - (cell % nx)) / nx) * CELL_MM + CELL_MM / 2 } : fb);
      const sa = pt(ca, pa), sb = pt(cb, pb), m = (d.lo + d.hi) / 2;
      doorways.push({
        id: `door/f${f}/${doorways.length}`, floor: f, partition: `part/f${f}/${w.name}`, a: idOf[qa], b: idOf[qb],
        x: (w.axis === 'x' ? m : w.at) / 1000, z: (w.axis === 'x' ? w.at : m) / 1000, width: (d.hi - d.lo) / 1000, axis: w.axis,
        ax: sa.x / 1000, az: sa.z / 1000, bx: sb.x / 1000, bz: sb.z / 1000,
      });
    }
  }
  return { partitions, rooms, doorways };
}

function applyCuts(c: Cand): void {
  for (const cut of c.arrangement.cuts ?? []) {
    const R = c.regions[cut.from];
    if (!R) continue;
    const horizontal = cut.side === 'east' || cut.side === 'west';
    const extent = horizontal ? R.x1 - R.x0 : R.z1 - R.z0;
    const size = snap(Math.max(mmOf(cut.size.min), Math.min(mmOf(cut.size.max), extent * cut.size.frac)));
    if (size > extent - 400) continue;
    let out: Bx, rest: Bx;
    if (cut.side === 'back') { out = { ...R, z0: R.z1 - size }; rest = { ...R, z1: R.z1 - size }; }
    else if (cut.side === 'front') { out = { ...R, z1: R.z0 + size }; rest = { ...R, z0: R.z0 + size }; }
    else if (cut.side === 'east') { out = { ...R, x0: R.x1 - size }; rest = { ...R, x1: R.x1 - size }; }
    else { out = { ...R, x1: R.x0 + size }; rest = { ...R, x0: R.x0 + size }; }
    c.regions[cut.from] = rest;
    // a band cut off the ROOM is cut off every base region (main, the pockets, east, west)
    if (cut.from === 'room') {
      for (const name of Object.keys(c.regions)) {
        if (name === 'room' || name === cut.out) continue;
        const q = c.regions[name];
        const clipped: Bx = { x0: Math.max(q.x0, rest.x0), x1: Math.min(q.x1, rest.x1), z0: Math.max(q.z0, rest.z0), z1: Math.min(q.z1, rest.z1) };
        if (clipped.x1 - clipped.x0 >= 400 && clipped.z1 - clipped.z0 >= 400) c.regions[name] = clipped; else delete c.regions[name];
      }
    }
    c.regions[cut.out] = out;
  }
  for (const [zid, names] of Object.entries(c.arrangement.zoneRects ?? {})) {
    const rects = names.map((n) => c.regions[n]).filter((r): r is Bx => !!r);
    if (rects.length > 0) c.zoneRects.set(zid, rects);
  }
}

interface Evaluated { c: Cand; res: Resolved; report: CandidateReport }

function evaluate(ctx: Ctx, base: FloorBase, program: FloorProgram, arrangement: Arrangement, rung: PlanRung, archetypeId: string, sparse: boolean, lift: ReturnType<typeof liftCandidateFor>): Evaluated {
  // (slice G) a `cells` program on a large floor is divided FIRST and furnished in the rooms; a candidate that cannot be
  // seated that way is tried again as one open floor — the division never costs a floor its program
  if (ctx.rooms && !sparse && DIVIDE_POLICY[program.id] === 'cells' && base.usableM2 >= DIVIDE_M2 && !(program.units && arrangement.id === 'units')) {
    const ev = evaluateOnce(ctx, base, program, arrangement, rung, archetypeId, sparse, lift, true);
    if (ev.report.status === 'valid') return ev;
  }
  return evaluateOnce(ctx, base, program, arrangement, rung, archetypeId, sparse, lift, false);
}
function evaluateOnce(ctx: Ctx, base: FloorBase, program: FloorProgram, arrangement: Arrangement, rung: PlanRung, archetypeId: string, sparse: boolean, lift: ReturnType<typeof liftCandidateFor>, cells: boolean): Evaluated {
  ctx.work.candidates++;
  const c = newCand(base, program, arrangement, ctx.turnSites);
  const id = `${rung}:${archetypeId}:${program.id}:${arrangement.id}`;
  applyCuts(c);
  const useUnits = !!program.units && arrangement.id === 'units';
  if (useUnits) cutUnits(c, program.units!, lift, ctx.rooms, ctx.storeys);
  if (cells) {
    if (!base.cells) { divideFloor(ctx, c, resolveAnchors(ctx, c), CELLS_PITCH_MM); base.cells = c.walls.map((w) => ({ ...w, doors: w.doors.map((d) => ({ ...d })) })); }
    else { c.walls.push(...base.cells.map((w) => ({ ...w, doors: w.doors.map((d) => ({ ...d })) }))); for (const w of c.walls) for (const d of w.doors) c.keeps.push(...doorAprons(w, d)); syncWalls(c); }
    c.divided = c.walls.length > 0;
  }
  // ZONES BY USABLE AREA: optional zones leave (highest dropOrder first) until what must be there fits.
  const usable = base.usableM2;
  const need = (): number => [...c.zones.values()].reduce((s, z) => s + z.minArea, 0);
  const mandatoryNeed = [...c.zones.values()].filter((z) => z.mandatory).reduce((s, z) => s + z.minArea, 0);
  const rep = (status: 'valid' | 'rejected', total = 0, parts: Record<string, number> = {}): CandidateReport => ({ id, rung, status, reasons: [...c.reasons], score: total, scoreParts: parts, pieces: c.placed.length, repairs: c.repairs });
  if (mandatoryNeed > usable) { c.reasons.push(`area:mandatory:${mandatoryNeed}m2>${usable.toFixed(1)}m2`); return { c, res: emptyResolved(base), report: rep('rejected') }; }
  if (!useUnits) {
    const optional = [...c.zones.values()].filter((z) => !z.mandatory).sort((a, b2) => (b2.dropOrder ?? 0) - (a.dropOrder ?? 0));
    for (const z of optional) { if (need() <= usable) break; c.zones.delete(z.id); c.dropped.push(z.id); c.reasons.push(`zone:dropped:${z.id}:area`); }
  }
  if (sparse) for (const z of [...c.zones.values()]) if (!z.mandatory) { c.zones.delete(z.id); c.dropped.push(z.id); }
  // PRIMARY KITS, zone by zone in priority order (a fill keeps its place within its zone).
  const band = sizeBand(base.usableM2);
  const fills = useUnits ? [] : arrangement.fills.filter((f) => !f.pass && c.zones.has(f.zone) && (!f.bands || f.bands.includes(band)));
  const order = [...new Set(fills.map((f) => f.zone))].sort((a, b2) => (c.zones.get(a)!.priority - c.zones.get(b2)!.priority));
  for (const zid of order) {
    let n = 0;
    for (const f of fills) if (f.zone === zid) n += runFill(ctx, c, f, zid, 'primary', sparse, -1);
    if (n === 0) {
      const z = c.zones.get(zid)!;
      if (z.mandatory) { c.reasons.push(`kit:no-site:${zid}`); return { c, res: emptyResolved(base), report: rep('rejected') }; }
      c.zones.delete(zid); c.dropped.push(zid); c.reasons.push(`zone:dropped:${zid}:no-site`);
    }
  }
  if (useUnits) {
    for (const u of c.units) {
      c.regions.unit = u.inner ?? u.rect;
      c.cursors = new Map([...c.cursors].filter(([k]) => !k.startsWith('unit:')));
      let n = 0;
      for (const f of program.units!.fills) if (!f.pass) n += runFill(ctx, c, f, u.zone, 'primary', sparse, u.index);
      if (n === 0) c.reasons.push(`unit:empty:${u.index}`);
    }
    delete c.regions.unit;
    if (!c.units.some((u) => c.insts.some((i) => i.zone === u.zone))) { c.reasons.push('units:none-seated'); return { c, res: emptyResolved(base), report: rep('rejected') }; }
  }
  const proof = prove(ctx, c);
  if (!proof.ok) return { c, res: proof.res, report: rep('rejected') };
  // (slice G) NOTHING IS SEALED: walls laid before the furniture (units, cells) stand only if every doorway is still
  // reached on both sides with the furniture in — else this candidate is not a room plan (the next arrangement, or the
  // same program on the open floor, is)
  if (c.walls.length > 0 && !doorsReached(c, proof.res)) { c.reasons.push('rooms:doorway-blocked'); return { c, res: proof.res, report: rep('rejected') }; }
  const sc = score(ctx, c, proof.res, id);
  return { c, res: proof.res, report: rep('valid', sc.total, sc.parts) };
}
function emptyResolved(base: FloorBase): Resolved {
  const n = base.nx * base.nz;
  return { cells: new Uint8Array(base.raster0), fl: { dist: new Int32Array(n).fill(-1), parent: new Int32Array(n).fill(-1), reached: 0 }, anchors: [], failed: [], nodeCells: new Map(), missingNodes: [] };
}

/** The candidate arrangements of a program for this floor, in their stable order (the first K). */
function arrangementsFor(ctx: Ctx, base: FloorBase, program: FloorProgram): Arrangement[] {
  const band = sizeBand(base.usableM2);
  const list: Arrangement[] = [];
  if (program.units) list.push({ id: 'units', fills: [] });
  // a builder of no tradition takes one of the program's by the building's own seed (so two neutral cantinas differ)
  let tradition = ctx.tradition;
  if (tradition === 'neutral') {
    const named = [...new Set(program.arrangements.flatMap((a) => a.traditions ?? []))].sort();
    if (named.length > 0) tradition = named[rngOf(seedOf(ctx.input.buildingId, ctx.seedNs + (ctx.salt.topology ?? ''), 0, program.id, 'topology')).int(named.length)];
  }
  for (const a of program.arrangements) {
    if (a.bands && !a.bands.includes(band)) continue;
    if (a.aspect === 'wide' && base.hw < base.hd * 1.5) continue;
    if (a.aspect === 'deep' && base.hd < base.hw * 1.5) continue;
    if (a.traditions && tradition !== 'neutral' && !a.traditions.includes(tradition)) continue;
    list.push(a);
  }
  return list.slice(0, ctx.K);
}

/** Dress the winner: its secondary fills, then its lived-in detail. A later piece is KEPT only if everything proven
 *  before it stays proven — every earlier anchor on its own standing cell, every node reachable — so the primary
 *  pieces and their anchors never move for it, and every prefix of the emitted order is a valid room. */
function dress(ctx: Ctx, ev: Evaluated, soft: FloorProgram | null = null): Resolved {
  const c = ev.c;
  let res = ev.res;
  const passes: Array<'secondary' | 'detail'> = ctx.tiers === 'primary' ? [] : ctx.tiers === 'secondary' ? ['secondary'] : ['secondary', 'detail'];
  const one = (f: Fill, zoneId: string, pass: 'secondary' | 'detail', unit: number, box?: Bx): void => {
    const before = c.insts.length;
    const placedBefore = c.placed, keepsBefore = c.keeps.length, cursorsBefore = new Map(c.cursors);
    // (slice U) THE WHOLE FILL GOES BACK — from a COPY. `tryPlace` pushes into `c.placed` in place, so `placedBefore`
    // holds this fill's pieces too, and a piece the drop below removes with its whole instance came back with
    // `placedBefore.filter` (its instance is no longer in the slice the filter reads): a withdrawn mirror stood on
    // a seat's standing cell. Below the storeys revision the old undo stands (a pinned room keeps its pieces).
    const instsSnap = ctx.storeys ? c.insts.slice() : null, placedSnap = ctx.storeys ? c.placed.slice() : null;
    runFill(ctx, c, f, zoneId, pass, false, unit);
    if (c.insts.length === before) return;
    const proof = (): Resolved => resolveAnchors(ctx, c, box ? { prev: res, box } : undefined);
    let next = proof();
    // a new piece nobody can use leaves again
    const fresh = new Set(c.insts.slice(before).map((i) => i.id));
    let dropped = false;
    for (const fa of next.failed) if (fresh.has(fa.inst) && !next.anchors.some((a) => a.member === fa.member) && c.placed.includes(fa.member)) { removeMember(c, fa.member); dropped = true; }
    if (dropped) next = proof();
    if (holds(res, next) && doorsReached(c, next)) { res = next; return; }
    // it took something proven (an anchor, a node, a doorway): the whole fill goes back
    if (instsSnap && placedSnap) { c.insts = instsSnap; c.placed = placedSnap; }
    else {
      const gone = new Set(c.insts.slice(before).map((i) => i.id));
      c.insts = c.insts.filter((i) => !gone.has(i.id));
      c.placed = placedBefore.filter((p) => !gone.has(p.inst));
    }
    c.keeps.length = keepsBefore; c.cursors = cursorsBefore;
    c.reasons.push(`${pass}:withdrawn:${zoneId}/${f.kits[0]}`);
  };
  // (slice U, the storeys revision) A UNIT'S LATER PASS IS PROVEN ONCE. A unit floor's soft layer is five fills in each
  // of up to eight units — a route proof per fill made a big tower's plan twice as long. Each unit's fills of a pass
  // are seated together and proven together; only where the unit's batch takes something proven is it undone and
  // tried again fill by fill (the old way). Units do not share ground, so a batch is one unit's.
  const batch = (fills: Fill[], zoneId: string, pass: 'secondary' | 'detail', unit: number, box: Bx): void => {
    if (fills.length === 0) return;
    const before = c.insts.length;
    const keepsBefore = c.keeps.length, cursorsBefore = new Map(c.cursors);
    const instsSnap = c.insts.slice(), placedSnap = c.placed.slice(); // (slice U) undone from a copy (see `one`)
    for (const f of fills) runFill(ctx, c, f, zoneId, pass, false, unit);
    if (c.insts.length === before) return;
    const proof = (): Resolved => resolveAnchors(ctx, c, { prev: res, box });
    let next = proof();
    const fresh = new Set(c.insts.slice(before).map((i) => i.id));
    let dropped = false;
    for (const fa of next.failed) if (fresh.has(fa.inst) && !next.anchors.some((a) => a.member === fa.member) && c.placed.includes(fa.member)) { removeMember(c, fa.member); dropped = true; }
    if (dropped) next = proof();
    if (holds(res, next) && doorsReached(c, next)) { res = next; return; }
    c.insts = instsSnap; c.placed = placedSnap;
    c.keeps.length = keepsBefore; c.cursors = cursorsBefore;
    for (const f of fills) one(f, zoneId, pass, unit, box);
  };
  /** A unit's patch box: its rectangle and a rim (its walls, its doorway, a strip of the corridor). */
  const RIM = 600;
  const boxOfUnit = (u: Cand['units'][number]): Bx => ({ x0: u.rect.x0 - RIM, x1: u.rect.x1 + RIM, z0: u.rect.z0 - RIM, z1: u.rect.z1 + RIM });
  let patched = false;
  for (const pass of passes) {
    if (c.units.length > 0) {
      for (const u of c.units) {
        c.regions.unit = u.inner ?? u.rect;
        c.cursors = new Map([...c.cursors].filter(([k]) => !k.startsWith('unit:')));
        if (ctx.storeys && c.walls.length > 0) { batch(c.program.units!.fills.filter((f) => f.pass === pass), u.zone, pass, u.index, boxOfUnit(u)); patched = true; }
        else for (const f of c.program.units!.fills) if (f.pass === pass) one(f, u.zone, pass, u.index);
      }
      delete c.regions.unit;
      // the floor is proven WHOLE again before anything outside the units is seated (exact distances, exact routes)
      if (patched) { const whole = resolveAnchors(ctx, c); if (!holds(res, whole)) c.reasons.push('storey:patch-drift'); res = whole; patched = false; }
      // (slice U, the storeys revision) the corridor the units leave is somewhere too: a bench, a lamp, something green
      if (ctx.storeys) for (const f of c.program.units!.common ?? []) if (f.pass === pass && c.zones.has(f.zone)) one(f, f.zone, pass, -1);
    } else {
      for (const f of c.arrangement.fills) if (f.pass === pass && c.zones.has(f.zone) && (!f.bands || f.bands.includes(sizeBand(c.base.usableM2)))) one(f, f.zone, pass, -1);
    }
  }
  // (slice U, the storeys revision) THE SOFT TOP-UP. A storey whose program is seated but leaves a resident fewer than
  // three places of two kinds — an archive that is all stacks, a small office floor that is one desk — takes its
  // family's soft pieces (interior_archetypes.ts `softStorey`: a seat, a bed or a desk, somewhere to put things, a
  // lamp) as LATER-PASS fills: each is kept only if everything proven before it stays proven, so the program's own
  // pieces and anchors never move for them and every prefix of the emitted order is still a valid room.
  if (soft && soft !== c.program && c.units.length === 0 && passes.length > 0 && !livable(res)) {
    const mine = soft.zones.filter((z) => !c.zones.has(z.id));
    for (const z of mine) c.zones.set(z.id, { ...z, mandatory: false });
    for (const f of soft.arrangements[0].fills) if (c.zones.has(f.zone) && (!f.pass || passes.includes(f.pass))) one(f, f.zone, f.pass ?? 'secondary', -1);
    for (const z of mine) if (!c.insts.some((i) => i.zone === z.id)) c.zones.delete(z.id);
  }
  return res;
}
/** WHAT A RESIDENT CAN USE on a floor, as the itinerary counts it (interior_life.ts `lifeRoomOf`, restated; the gate
 *  compares the two): a bed or a seat with its socket, a work point that is not guarded, a public standing point.
 *  LIVABLE = at least three such places of at least two kinds. */
export const LIVABLE = { places: 3, kinds: 2 } as const;
/** The resident's places among a floor's anchors, by use (`lie` · `sit` · `work` · `stand`). */
export function residentPlaces(anchors: ReadonlyArray<Pick<DescAnchor, 'kind' | 'access' | 'seat'>>): Record<string, number> {
  const by: Record<string, number> = {};
  for (const a of anchors) {
    let k = '';
    if (a.seat && (a.kind === 'bed' || a.kind === 'seat')) k = a.seat.pose === 'lie' ? 'lie' : 'sit';
    else if (a.kind === 'work' && a.access !== 'guarded') k = 'work';
    else if (a.kind === 'stand' && a.access === 'public') k = 'stand';
    if (k) by[k] = (by[k] ?? 0) + 1;
  }
  return by;
}
/** (slice U) A reason that says a candidate lost something its program asked for. */
function lossy(q: string): boolean { return q.startsWith('repair:') || q.startsWith('unreachable:') || q.includes(':no-site') || q.startsWith('unit:empty') || q.startsWith('zone:missing'); }
/** (slice U) What a candidate KEEPS, in order of what matters: valid, its units, its zones, a resident's places. */
function keptKey(ev: Evaluated): number[] {
  if (ev.report.status !== 'valid') return [0, 0, 0, 0];
  const dropped = ev.c.reasons.filter((q) => q.startsWith('repair:dropped-zone') || q.startsWith('zone:missing')).length;
  const by = residentPlaces(ev.res.anchors);
  return [1, ev.c.units.length, -dropped, Object.values(by).reduce((n, v) => n + v, 0)];
}
/** (slice U) `a` keeps strictly more than `b` (lexicographic over `keptKey`). */
function betterKept(a: Evaluated, b: Evaluated): boolean {
  const x = keptKey(a), y = keptKey(b);
  for (let i = 0; i < x.length; i++) if (x[i] !== y[i]) return x[i] > y[i];
  return false;
}
function livable(res: Resolved): boolean {
  const by = residentPlaces(res.anchors);
  const kinds = Object.keys(by);
  return kinds.reduce((n, k) => n + by[k], 0) >= LIVABLE.places && kinds.length >= LIVABLE.kinds;
}

// ─── From a candidate to a descriptor floor ─────────────────────────────────────────────────────────────────────────

function polyline(b: FloorBase, fl: Flood, cell: number): { points: Array<[number, number]>; lengthM: number } {
  const cells = pathCells(fl, cell).reverse();
  const pts: Array<[number, number]> = [];
  let pdx = 0, pdz = 0;
  for (let k = 0; k < cells.length; k++) {
    const i = cells[k] % b.nx, j = (cells[k] - i) / b.nx;
    const x = (-b.hw + i * CELL_MM + CELL_MM / 2) / 1000, z = (-b.hd + j * CELL_MM + CELL_MM / 2) / 1000;
    if (k === 0 || k === cells.length - 1) { pts.push([x, z]); }
    else {
      const ni = cells[k + 1] % b.nx, nj = (cells[k + 1] - ni) / b.nx;
      const dx = ni - i, dz = nj - j;
      if (dx !== pdx || dz !== pdz) pts.push([x, z]);
      pdx = dx; pdz = dz;
    }
  }
  return { points: pts, lengthM: (Math.max(0, cells.length - 1) * CELL_MM) / 1000 };
}

const TIER_RANK: Record<PieceTier, number> = { primary: 0, secondary: 2, detail: 3, owned: 4 };

/** THE PRIORITY ORDER (the pack prefix law): each zone's first kit's essential pieces in zone order, then the other
 *  primaries, then secondary, then detail — and never a piece before what it stands on or needs. */
function emitOrder(c: Cand): Placed[] {
  const firstInstOfZone = new Map<string, number>();
  for (const i of c.insts) if (!firstInstOfZone.has(i.zone) && i.pass === 'primary') firstInstOfZone.set(i.zone, i.id);
  const seq = new Map<Placed, number>(c.placed.map((p, k) => [p, k]));
  const rank = (p: Placed): number => {
    if (p.tier !== 'primary') return TIER_RANK[p.tier];
    return firstInstOfZone.get(p.zone) === p.inst && !p.optional ? 0 : 1;
  };
  const zp = (p: Placed): number => c.zones.get(p.zone)?.priority ?? 99;
  // within a tier, what the PRIMARY pass seated comes before what a later pass added (a dining set's own extra chairs
  // before the study's chair): a later pass can then never slip a piece in front of an earlier one — the order of what
  // was there, and so every same-key rank, is the same with or without the later passes
  const passOf = new Map<number, number>(c.insts.map((i) => [i.id, i.pass === 'primary' ? 0 : i.pass === 'secondary' ? 1 : 2]));
  const pr = (p: Placed): number => passOf.get(p.inst) ?? 0;
  const sorted = [...c.placed].sort((a, b) => rank(a) - rank(b) || pr(a) - pr(b) || zp(a) - zp(b) || seq.get(a)! - seq.get(b)!);
  // dependencies: a piece moves to just after the last thing it needs
  const out: Placed[] = [];
  const done = new Set<Placed>();
  const needsOf = (p: Placed): Placed[] => {
    const inst = c.insts.find((i) => i.id === p.inst);
    return p.needRoles.map((r) => inst?.seated.get(r)).filter((q): q is Placed => !!q);
  };
  const waiting: Placed[] = [];
  const push = (p: Placed): void => {
    out.push(p); done.add(p);
    for (let k = 0; k < waiting.length; k++) {
      if (needsOf(waiting[k]).every((q) => done.has(q))) { const w = waiting.splice(k, 1)[0]; push(w); k = -1; }
    }
  };
  for (const p of sorted) { if (needsOf(p).every((q) => done.has(q))) push(p); else waiting.push(p); }
  out.push(...waiting); // (a cycle cannot be authored; anything left keeps its sorted place)
  return out;
}

function buildFloor(ctx: Ctx, c: Cand, res: Resolved, lift: ReturnType<typeof liftCandidateFor>): DescFloor {
  const b = c.base;
  const ordered = emitOrder(c);
  // stable slot ids: `<zone>/<kit>/<role>#n`, n counted per (zone, kit, role) in seating order
  const counter = new Map<string, number>();
  for (const p of [...c.placed]) { const k = `${p.zone}/${p.kit}/${p.role}`; const n = counter.get(k) ?? 0; counter.set(k, n + 1); p.slot = `${k}#${n}`; }
  const pieces: DescPiece[] = ordered.map((p, order) => toPiece(p, b.f, order, c));
  for (const o of b.owned) pieces.push(toPiece(o, b.f, pieces.length, null));
  const anchors: DescAnchor[] = res.anchors.map((a) => {
    const { inst, member, required, cell, ...rest } = a;
    void inst; void required; void cell;
    return { ...rest, piece: member?.slot ?? '' };
  });
  const zones: DescZone[] = [];
  for (const z of [...c.zones.values()].sort((a, b2) => a.priority - b2.priority || (a.id < b2.id ? -1 : 1))) {
    const boxes = zoneBoxes(c, z.id);
    const isArrival = z.id === 'arrival';
    const rects = boxes.length > 0 ? boxes : isArrival ? [{ x0: -1200, x1: 1200, z0: -b.hd, z1: Math.min(b.hd, -b.hd + b.doorDepth) }] : [];
    if (rects.length === 0) continue;
    const unit = c.units.find((u) => u.zone === z.id);
    zones.push({
      id: z.id, role: z.role, floor: b.f, access: z.access, enclosure: z.enclosure, mandatory: z.mandatory,
      rects: rects.map(rectOf), areaM2: Math.round(rects.reduce((s, r) => s + (r.x1 - r.x0) * (r.z1 - r.z0), 0) / 1e4) / 100,
      kits: [...new Set(c.insts.filter((i) => i.zone === z.id).map((i) => i.kit.spec.id))],
      ...(unit ? { unitId: `unit/f${b.f}/${unit.index}` } : {}),
    });
  }
  // Route nodes and the tree.
  const nodes: RouteNode[] = [{ id: b.f === 0 ? 'entry' : 'landing', kind: b.f === 0 ? 'entry' : 'landing', floor: b.f, x: b.entry.x / 1000, z: b.entry.z / 1000 }];
  const routes: RouteEdge[] = [];
  const root = nodes[0].id;
  for (const n of b.nodes) {
    const cell = res.nodeCells.get(n.id);
    if (cell === undefined) continue;
    const pl = polyline(b, res.fl, cell);
    const end = pl.points[pl.points.length - 1] ?? [n.x / 1000, n.z / 1000];
    nodes.push({ id: n.id, kind: n.kind, floor: b.f, x: end[0], z: end[1] });
    routes.push({ from: root, to: n.id, lengthM: pl.lengthM, points: pl.points });
  }
  // (slice G) the rooms the walls make, and the doorways between them
  const rs = roomsOf(c, res);
  // Units: the threshold on the corridor side, the anchors inside.
  const units: DescUnit[] = [];
  for (const u of c.units) {
    if (!anchors.some((a) => a.zone === u.zone && a.kind === 'bed')) continue; // a unit is a room with a bed
    const mid = snap((u.rect.z0 + u.rect.z1) / 2);
    const cell = nearestFree(b, res.cells, u.rect.x0 - 450, mid, 1500, res.fl.dist);
    if (cell < 0) continue;
    const i = cell % b.nx, j = (cell - i) / b.nx;
    const ax = (-b.hw + i * CELL_MM + CELL_MM / 2) / 1000, az = (-b.hd + j * CELL_MM + CELL_MM / 2) / 1000;
    const tid = `${u.zone}/door/threshold#0`;
    anchors.push({ id: tid, zone: u.zone, floor: b.f, kind: 'threshold', x: u.rect.x0 / 1000, z: mid / 1000, ax, az, facing: quantYaw(Math.atan2(u.rect.x0 / 1000 - ax, mid / 1000 - az)), envelope: { hx: 0.4, hz: 0.5 }, access: 'guarded', routeM: (res.fl.dist[cell] * CELL_MM) / 1000 });
    units.push({
      unitId: `unit/f${b.f}/${u.index}`, floor: b.f, kind: c.program.units!.kind, zone: u.zone, rect: rectOf(u.rect), threshold: tid,
      anchors: anchors.filter((a) => a.zone === u.zone && a.kind !== 'threshold').map((a) => a.id), pieces: pieces.filter((p) => p.zone === u.zone).map((p) => p.slot),
    });
  }
  // A home's spare room is a unit too: its zone's bounds, its threshold the edge the route comes in by.
  const spare = c.zones.get('spare');
  if (spare && c.units.length === 0 && c.insts.some((i) => i.zone === 'spare')) {
    // the rect is the room's ESSENTIALS (primary-tier pieces): stable whatever a pack or the detail pass adds
    let bb: Bx | null = c.zoneRects.get('spare')?.[0] ?? null;
    if (!bb) for (const p of c.placed) {
      if (p.zone !== 'spare' || p.tier !== 'primary' || !p.blocksWalk) continue;
      const q = boxOf(p);
      bb = bb ? { x0: Math.min(bb.x0, q.x0), x1: Math.max(bb.x1, q.x1), z0: Math.min(bb.z0, q.z0), z1: Math.max(bb.z1, q.z1) } : q;
    }
    const bed = anchors.find((a) => a.zone === 'spare' && a.kind === 'bed');
    if (bb && bed) {
      const tid = 'spare/door/threshold#0';
      // (slice G) an ENCLOSED spare room's threshold is its doorway's outer side; an open one keeps the bed's approach
      const sid = roomIdOf(b.f, 'spare');
      const dw = rs.doorways.find((q) => q.a === sid || q.b === sid);
      const out = dw ? (dw.a === sid ? { x: dw.bx, z: dw.bz } : { x: dw.ax, z: dw.az }) : null;
      const oc = out ? nearestFree(b, res.cells, mmOf(out.x), mmOf(out.z), 300, res.fl.dist) : -1;
      if (dw && out && oc >= 0) anchors.push({ id: tid, zone: 'spare', floor: b.f, kind: 'threshold', x: dw.x, z: dw.z, ax: out.x, az: out.z, facing: quantYaw(Math.atan2(dw.x - out.x, dw.z - out.z)), envelope: { hx: 0.4, hz: 0.5 }, access: 'guarded', routeM: (res.fl.dist[oc] * CELL_MM) / 1000 });
      else anchors.push({ id: tid, zone: 'spare', floor: b.f, kind: 'threshold', x: bed.ax, z: bed.az, ax: bed.ax, az: bed.az, facing: bed.facing, envelope: { hx: 0.4, hz: 0.5 }, access: 'guarded', routeM: bed.routeM });
      units.push({ unitId: `unit/f${b.f}/0`, floor: b.f, kind: 'spare', zone: 'spare', rect: rectOf(bb), threshold: tid, anchors: anchors.filter((a) => a.zone === 'spare' && a.kind !== 'threshold').map((a) => a.id), pieces: pieces.filter((p) => p.zone === 'spare').map((p) => p.slot) });
      const dz = zones.find((z) => z.id === 'spare'); if (dz) dz.unitId = `unit/f${b.f}/0`;
    }
  }
  // The lift's call post is an anchor on every floor it is reachable from.
  if (lift && res.nodeCells.has('lift-call')) {
    anchors.push({ id: 'lift/call/lift-call#0', zone: 'lift', floor: b.f, kind: 'lift-call', x: (lift.box.x0 + lift.box.x1) / 2000, z: (lift.box.z0 + lift.box.z1) / 2000, ax: lift.call.x / 1000, az: lift.call.z / 1000, facing: lift.call.facing, envelope: { hx: 0.5, hz: 0.5 }, access: 'public', routeM: (res.fl.dist[res.nodeCells.get('lift-call')!] * CELL_MM) / 1000 });
  }
  for (const z of zones) {
    const zr = z.rects[0];
    const cell = nearestFree(b, res.cells, mmOf((zr.x0 + zr.x1) / 2), mmOf((zr.z0 + zr.z1) / 2), 4000, res.fl.dist);
    if (cell < 0) continue;
    const pl = polyline(b, res.fl, cell);
    const end = pl.points[pl.points.length - 1];
    nodes.push({ id: `zone:${z.id}`, kind: 'zone', floor: b.f, x: end[0], z: end[1] });
    routes.push({ from: root, to: `zone:${z.id}`, lengthM: pl.lengthM, points: pl.points });
  }
  for (const a of res.anchors) {
    const pl = polyline(b, res.fl, a.cell);
    nodes.push({ id: a.id, kind: 'anchor', floor: b.f, x: a.ax, z: a.az });
    routes.push({ from: root, to: a.id, lengthM: pl.lengthM, points: pl.points });
  }
  // a room that IS a unit says so (slice R: the tenant's door)
  for (const u of units) { const room = rs.rooms.find((q) => q.zone === u.zone); if (room) room.unitId = u.unitId; }
  return {
    floor: b.f, floorY: b.f === 0 ? 0.08 : b.f * FLOOR_H, program: c.program.id, source: 'planned', usableM2: Math.round(b.usableM2 * 100) / 100,
    pieces, zones, anchors, units, partitions: rs.partitions, rooms: rs.rooms, doorways: rs.doorways, routeNodes: nodes, routes,
  };
}
function toPiece(p: Placed, floor: number, order: number, c: Cand | null): DescPiece {
  const meta = pieceMeta(p.key);
  const inst = c?.insts.find((i) => i.id === p.inst);
  const needs = p.needRoles.map((r) => inst?.seated.get(r)?.slot).filter((s): s is string => !!s);
  const on = p.onRole ? inst?.seated.get(p.onRole)?.slot : undefined;
  return {
    slot: p.slot, key: p.key, floor, order, x: p.exact ? p.exact.x : p.x / 1000, z: p.exact ? p.exact.z : p.z / 1000, yaw: p.yaw, lift: p.lift / 1000, tier: p.tier,
    zone: p.zone, kit: p.kit, role: p.role, blocksWalk: p.blocksWalk,
    coverHeight: meta?.coverHeight ?? 0, blocksSight: !!meta?.blocksSight && p.blocksWalk,
    half: meta?.half ?? { w: 0.5, d: 0.5 },
    ...(on ? { on } : {}), ...(needs.length > 0 ? { needs } : {}),
  };
}

/** A floor with no furniture: its entry, its nodes, its routes (so the links and the lift still read). */
function bareFloor(ctx: Ctx, base: FloorBase, program: string, source: DescFloor['source'], lift: ReturnType<typeof liftCandidateFor>): DescFloor {
  const c = newCand(base, { id: program, zones: [], arrangements: [] }, { id: 'none', fills: [] });
  const res = resolveAnchors(ctx, c);
  const fl = buildFloor(ctx, c, res, lift);
  return { ...fl, program, source };
}

// ─── The legacy plan as a descriptor floor ──────────────────────────────────────────────────────────────────────────

function legacyFloor(ctx: Ctx, base: FloorBase, purpose: InteriorPurpose, lift: ReturnType<typeof liftCandidateFor>, hard: string[]): DescFloor {
  // TODAY'S PLAN, UNTOUCHED: the same calls the visit makes on the shell's own frame — `purposePlan` (a settlement
  // building, `furnishByPurpose`), and for a deed's ground floor `offTheConsole(purposePlan)` (`furnishDeed` →
  // `packPlan`). So a legacy slot's index IS the index today's `pack:<floor>:<index>:<key>` storage key carries.
  const raw: PlanSlot[] = purposePlan(purpose, base.lawFrame, base.f);
  const offConsole: PlanSlot[] = ctx.input.buildingId.startsWith('deed:') && base.f === 0 ? offTheConsole(raw, base.lawFrame) : raw;
  // (slice U, the storeys revision) THE LIFT RESERVE IS EVERY PLAN'S: where the ladder falls to today's plan, a piece
  // that would stand in the shaft's cell, or block its landing apron or the stair landing's, is dropped — the
  // `offTheConsole` pattern, BEFORE the slots are numbered. Below the storeys revision the plan is taken untouched
  // (a room pinned there keeps its pieces; slice L's shaft veto answers for it).
  const slotBox = (sl: PlanSlot): Bx => {
    const h = drawnHalf(sl.key, sl.yaw);
    const hx = Math.ceil(h.hx * 1000 - 1e-6), hz = Math.ceil(h.hz * 1000 - 1e-6);
    return { x0: mmOf(sl.x) - hx, x1: mmOf(sl.x) + hx, z0: mmOf(sl.z) - hz, z1: mmOf(sl.z) + hz };
  };
  let slots: PlanSlot[] = !ctx.storeys ? offConsole : offConsole.filter((sl) => {
    const bx = slotBox(sl);
    if (lift && meets(bx, lift.box)) return false;
    if (!(pieceMeta(sl.key)?.blocksWalk ?? true)) return true;
    return !base.keep.some((k) => meets(bx, k));
  });
  // (slice U) THE WAY TO THE LIFT IS KEPT: where today's pieces still wall a required node (the lift's call post, the
  // stair's foot) off from the door, the pieces standing on the shortest free way to it stand down too — before the
  // slots are numbered. A small floor's legacy plan once sealed its call post this way.
  if (ctx.storeys) {
    const seedOf0 = (cells: Uint8Array): number => { const k = Raster.index(base, base.entry.x, base.entry.z); return k >= 0 && !cells[k] ? k : nearestFree(base, cells, base.entry.x, base.entry.z, 1500); };
    const blocking = (list: PlanSlot[]): Uint8Array => {
      const cells = new Uint8Array(base.raster0);
      for (const sl of list) if (pieceMeta(sl.key)?.blocksWalk ?? true) Raster.block(cells, base, slotBox(sl), GROW);
      return cells;
    };
    const cells = blocking(slots);
    const fl = flood(base, cells, seedOf0(cells), ctx.work);
    const sealed = base.nodes.filter((n) => n.required && nearestFree(base, cells, n.x, n.z, 900, fl.dist) < 0);
    if (sealed.length > 0) {
      const fl0 = flood(base, base.raster0, seedOf0(base.raster0), ctx.work);
      const way = new Uint8Array(base.nx * base.nz);
      for (const n of sealed) for (let k = nearestFree(base, base.raster0, n.x, n.z, 900, fl0.dist), guard = 0; k >= 0 && guard < way.length; k = fl0.parent[k], guard++) way[k] = 1;
      slots = slots.filter((sl) => !(pieceMeta(sl.key)?.blocksWalk ?? true) || !Raster.touches(way, base, slotBox(sl), GROW));
    }
  }
  const c = newCand(base, { id: `legacy.${purpose}.${base.f === 0 ? 'ground' : 'upper'}`, zones: [{ id: 'legacy', role: purpose, mandatory: false, minArea: 0, adjacentTo: [], separatedFrom: [], access: 'public', enclosure: 'zone', requires: [], priority: 0 }], arrangements: [] }, { id: 'legacy', fills: [] });
  c.zoneRects.set('legacy', [{ x0: -base.hw, x1: base.hw, z0: -base.hd, z1: base.hd }]);
  const placed: Placed[] = slots.map((s, index) => {
    const meta = pieceMeta(s.key);
    const h = drawnHalf(s.key, s.yaw);
    return {
      key: s.key, x: mmOf(s.x), z: mmOf(s.z), yaw: s.yaw, q: 0, hx: Math.ceil(h.hx * 1000 - 1e-6), hz: Math.ceil(h.hz * 1000 - 1e-6), lift: 0, tier: 'primary' as PieceTier,
      zone: 'legacy', kit: 'legacy', role: s.key, inst: -2, blocksWalk: meta?.blocksWalk ?? true, underlay: meta?.underlay ?? false, needRoles: [], optional: false, slot: `legacy/${base.f}/${index}`,
    };
  });
  c.placed = placed;
  const res = resolveAnchors(ctx, c);
  // best-effort sockets: a seat, a bed and a store each get an anchor where a free cell is in reach
  const cells = res.cells;
  const anchors: DescAnchor[] = [];
  const count = new Map<string, number>();
  placed.forEach((p) => {
    const meta = pieceMeta(p.key);
    if (!meta?.use) return;
    const kind: AnchorKind | null = meta.use === 'sit' ? 'seat' : meta.use === 'lie' ? 'bed' : meta.use === 'storage' ? 'storage' : null;
    if (!kind) return;
    const cell = nearestFree(base, cells, p.x, p.z, Math.round(meta.reach * 1000), res.fl.dist);
    if (cell < 0) return;
    const i = cell % base.nx, j = (cell - i) / base.nx;
    const ax = (-base.hw + i * CELL_MM + CELL_MM / 2) / 1000, az = (-base.hd + j * CELL_MM + CELL_MM / 2) / 1000;
    const n = count.get(kind) ?? 0; count.set(kind, n + 1);
    const a: DescAnchor = { id: `legacy/legacy/${kind}#${n}`, zone: 'legacy', floor: base.f, kind, x: p.x / 1000, z: p.z / 1000, ax, az, facing: quantYaw(Math.atan2(p.x / 1000 - ax, p.z / 1000 - az)), envelope: { hx: 0.3, hz: 0.3 }, piece: p.slot, access: 'public', routeM: (res.fl.dist[cell] * CELL_MM) / 1000 };
    if (meta.sockets.length > 0) a.seat = { pose: meta.sockets[0].pose, eye: meta.sockets[0].eye, x: p.x / 1000, z: p.z / 1000, yaw: p.yaw };
    anchors.push(a);
  });
  const fl = buildFloorLegacy(ctx, c, res, placed, slots, anchors, lift);
  // the baseline is retained whatever it fails; what it fails is said
  for (let i = 0; i < placed.length; i++) for (let j = i + 1; j < placed.length; j++) {
    if (placed[i].blocksWalk && placed[j].blocksWalk && meets(boxOf(placed[i]), boxOf(placed[j]))) hard.push(`legacy:f${base.f}:overlap:${placed[i].slot}+${placed[j].slot}`);
  }
  for (const p of placed) if (p.blocksWalk) for (const k of [...base.keep, ...base.solids]) if (meets(boxOf(p), k)) { hard.push(`legacy:f${base.f}:reserved:${p.slot}`); break; }
  return fl;
}
function buildFloorLegacy(ctx: Ctx, c: Cand, res: Resolved, placed: Placed[], slots: PlanSlot[], anchors: DescAnchor[], lift: ReturnType<typeof liftCandidateFor>): DescFloor {
  const b = c.base;
  const saved = c.placed;
  c.placed = [];
  const shell = buildFloor(ctx, c, res, lift); // nodes, routes, the lift anchor
  c.placed = saved;
  const pieces: DescPiece[] = placed.map((p, order) => ({ ...toPiece(p, b.f, order, null), x: slots[order].x, z: slots[order].z, yaw: slots[order].yaw }));
  for (const o of b.owned) pieces.push(toPiece(o, b.f, pieces.length, null));
  const routes = [...shell.routes], nodes = [...shell.routeNodes];
  for (const a of anchors) {
    const cell = Raster.index(b, mmOf(a.ax), mmOf(a.az));
    if (cell < 0) continue;
    const pl = polyline(b, res.fl, cell);
    nodes.push({ id: a.id, kind: 'anchor', floor: b.f, x: a.ax, z: a.az });
    routes.push({ from: nodes[0].id, to: a.id, lengthM: pl.lengthM, points: pl.points });
  }
  return {
    floor: b.f, floorY: shell.floorY, program: c.program.id, source: 'legacy', usableM2: shell.usableM2, pieces,
    zones: [{ id: 'legacy', role: c.program.zones[0]?.role ?? 'legacy', floor: b.f, access: 'public', enclosure: 'zone', mandatory: false, rects: [{ x0: -b.hw / 1000, x1: b.hw / 1000, z0: -b.hd / 1000, z1: b.hd / 1000 }], areaM2: Math.round((4 * b.hw * b.hd) / 1e4) / 100, kits: ['legacy'] }],
    anchors: [...anchors, ...shell.anchors], units: [], partitions: [], rooms: shell.rooms, doorways: [], routeNodes: nodes, routes,
  };
}

// ─── planInterior ────────────────────────────────────────────────────────────────────────────────────────────────────

/** The catalog's signature: every key and its dims. */
export function catalogSignature(): string {
  syncCatalog();
  return hash128Hex(Object.keys(FURNITURE).sort().map((k) => `${k}:${FURNITURE[k].w}:${FURNITURE[k].h}:${FURNITURE[k].d}:${FURNITURE[k].cat}`).join('|')).slice(0, 16);
}
function shellSignature(input: PlanInput, spec: InteriorSpec): string {
  const cls = input.key === 'deed:guild_hall' || input.key === 'hive:store' ? input.key : 'plain';
  return `${mmOf(spec.hw)}x${mmOf(spec.hd)}x${spec.floors}:${mmOf(spec.stair.x)},${mmOf(spec.stair.z)},${mmOf(spec.stair.w)},${mmOf(spec.stair.d)}:${cls}`;
}

/** Owned content that seals mandatory circulation: which node is cut off (empty = none). */
function ownedConflict(ctx: Ctx, base: FloorBase): string[] {
  if (base.owned.length === 0) return [];
  const seed = Raster.index(base, base.entry.x, base.entry.z);
  const fl = flood(base, base.raster0, seed >= 0 && !base.raster0[seed] ? seed : -1, ctx.work);
  const out: string[] = [];
  if (fl.reached === 0) return ['entry'];
  for (const n of base.nodes) if (n.required && nearestFree(base, base.raster0, n.x, n.z, 900, fl.dist) < 0) out.push(n.id);
  // the room beyond the door corridor
  let beyond = false;
  const j0 = Math.min(base.nz - 1, Math.ceil(base.doorDepth / CELL_MM));
  for (let j = j0; j < base.nz && !beyond; j++) for (let i = 0; i < base.nx; i++) if (fl.dist[j * base.nx + i] >= 0) { beyond = true; break; }
  if (!beyond && base.f === 0) out.push('room');
  return out;
}

export function planInterior(input: PlanInput): PlanResult {
  syncCatalog();
  const opts = input.options ?? {};
  const work: PlanReport['work'] = { candidates: 0, repairs: 0, bfs: 0, cells: 0, placements: 0, tries: 0 };
  const deed = input.buildingId.startsWith('deed:');
  const stairZone = opts.stairZone ?? DEFAULT_STAIR_ZONE;
  const archetype: InteriorArchetype = (opts.archetype ? ARCHETYPES[opts.archetype] : undefined) ?? archetypeFor(input.key, input.purpose, input.buildingId);
  const legacyAsked = input.algorithm === 'legacy' || !!archetype.legacy;
  const tradition = input.tradition ?? defaultTradition(input.key);
  const layoutRevision = opts.layoutRevision ?? (input.algorithm === 'legacy' ? 0 : 1);
  // THE SHELL THIS LAYOUT WAS MADE ROUND (slice U): the shaft's rule is asked BY NAME — `clear` from the storeys
  // revision on in a shell that is not a deed's, `classic` everywhere else (building_interiors.ts THE CLEAR-LANDING RULE)
  const liftRule = liftRuleOf(input.key, layoutRevision);
  const spec = interiorSpec(input.w, input.h, input.d, input.key, { liftRule });
  const seedNs = `${archetype.id}.${archetype.revision}/L${layoutRevision}`;
  const ctx: Ctx = { input, spec, work, seedNs, salt: opts.streamSalt ?? {}, tiers: opts.tiers ?? 'all', K: Math.max(1, Math.min(PLAN_K, opts.K ?? PLAN_K)), R: Math.max(0, Math.min(PLAN_R, opts.R ?? PLAN_R)), tradition, rooms: layoutRevision >= ROOMS_LAYOUT_REVISION && input.algorithm !== 'legacy', storeys: layoutRevision >= STOREYS_LAYOUT_REVISION && input.algorithm !== 'legacy' && !archetype.legacy, turnSites: 0 };
  const lawFrame: PlanFrame = { hw: spec.hw, hd: spec.hd, stair: spec.stair };
  const lift = liftCandidateFor(spec, deed, lawFrame, liftRule);
  const reasons: string[] = [];
  const candidates: CandidateReport[] = [];
  const reserved: ReservedRegion[] = [];
  const hard: string[] = [];
  const floors: DescFloor[] = [];
  let status: PlanStatus = 'planned', rung: PlanRung = 'full', chosen = '';
  const legacyPurpose = archetype.legacy ?? (input.algorithm === 'legacy' ? input.purpose : archetype.legacyPurpose);
  const bases: FloorBase[] = [];
  for (let f = 0; f < spec.floors; f++) { const b = buildBase(spec, f, input, stairZone, lift, ctx.storeys); bases.push(b); reserved.push(...b.reserved); }

  if (legacyAsked) {
    status = 'legacy'; rung = 'legacy'; chosen = `legacy:${legacyPurpose}`;
    for (let f = 0; f < spec.floors; f++) floors.push(f < LEGACY_FLOORS ? legacyFloor(ctx, bases[f], legacyPurpose, lift, hard) : bareFloor(ctx, bases[f], 'unprogrammed', 'empty', lift));
    if (floors.every((fl) => fl.pieces.length === 0)) { status = 'empty'; rung = 'empty'; reasons.push('empty:legacy-plan-fits-nothing'); }
  } else {
    // OWNED CONTENT THAT SEALS THE ROOM: say so, add nothing, move nothing.
    const sealed = ownedConflict(ctx, bases[0]);
    if (sealed.length > 0) {
      status = 'conflict'; rung = 'empty'; chosen = 'conflict';
      for (const s of sealed) reasons.push(`conflict:owned-blocks:${s}`);
      for (let f = 0; f < spec.floors; f++) floors.push(bareFloor(ctx, bases[f], 'conflict', 'empty', lift));
    } else {
      // the rotation the middle storeys are dealt from: the building's own (its topology stream)
      const storeyStart = ctx.storeys ? rngOf(seedOf(ctx.input.buildingId, ctx.seedNs + (ctx.salt.topology ?? ''), 0, 'storeys', 'topology')).int(1 << 16) : 0;
      for (let f = 0; f < spec.floors; f++) {
        const base = bases[f];
        /** Every valid candidate of the last program tried (the storeys revision's second look — see below). */
        let alternates: Evaluated[] = [];
        const tryProgram = (a: InteriorArchetype, r: PlanRung, sparse: boolean, given?: FloorProgram): Evaluated | null => {
          alternates = [];
          const program = given ?? programFor(a, f);
          if (!program) return null;
          let best: Evaluated | null = null;
          const valid: Evaluated[] = [];
          for (const arr of arrangementsFor(ctx, base, program)) {
            // (slice U) above the ground a storey's fills take their sites in the storey's own order (siblings, not
            // copies). Where that order cost the floor something the AUTHOR's order keeps (a unit, a zone, a resident's
            // place), a second seeded order is tried, and only if the author's still keeps more does it stand — never
            // on score alone: the authored order always scores best, and picking it by score made every sibling a copy.
            let ev: Evaluated;
            if (ctx.storeys && f >= 1) {
              ctx.turnSites = 1;
              ev = evaluate(ctx, base, program, arr, r, a.id, sparse, lift);
              if (ev.report.status !== 'valid' || ev.c.reasons.some(lossy)) {
                ctx.turnSites = 0;
                const plain = evaluate(ctx, base, program, arr, r, a.id, sparse, lift);
                if (betterKept(plain, ev)) {
                  ctx.turnSites = 2;
                  const again = evaluate(ctx, base, program, arr, r, a.id, sparse, lift);
                  ev = betterKept(plain, again) ? plain : again;
                }
              }
            } else {
              ctx.turnSites = 0;
              ev = evaluate(ctx, base, program, arr, r, a.id, sparse, lift);
            }
            ctx.turnSites = 0;
            ev.report.id = `f${f}:${ev.report.id}`;
            candidates.push(ev.report);
            if (ev.report.status !== 'valid') continue;
            valid.push(ev);
            if (!best || ev.report.score > best.report.score) best = ev;
          }
          alternates = valid;
          // (slice U) ROOMS PER UNIT ALWAYS BEAT A SHARED FLOOR — whatever the shared floor scores (the storeys
          // revision's shared flat is a good room; a unit floor that can be cut still is one: rentals key on its units)
          const unitFloor = ctx.storeys ? valid.find((v) => v.report.id.endsWith(':units')) : undefined;
          if (unitFloor) return unitFloor;
          if (!best || valid.length < 2 || best.report.id.endsWith(':units')) return best;
          // NEAR-BEST BY THE BUILDING'S OWN SEED: arrangements within a small margin of the best are equally good rooms;
          // which one a building gets is its topology stream's — two houses of one deed type are not copies. Rooms
          // per unit always beat a shared floor (never traded for variety).
          const margin = Math.max(20, Math.round(best.report.score * 0.12));
          // (slice U, the storeys revision) above the ground an arrangement that KEEPS what the best keeps (its units,
          // its zones, a resident's places) is as good a room — on a small plate the score separates layouts a body
          // cannot tell apart, and a tower's storeys would all be the one top scorer
          const bestKept = ctx.storeys && f >= 1 ? keptKey(best).join(',') : '';
          const near = valid.filter((v) => !v.report.id.endsWith(':units') && (v.report.score >= best!.report.score - margin || (bestKept !== '' && keptKey(v).join(',') === bestKept)));
          return near[rngOf(seedOf(ctx.input.buildingId, ctx.seedNs + (ctx.salt.topology ?? ''), f, program.id, 'topology')).int(near.length)];
        };
        // (slice U, the storeys revision) EVERY STOREY HAS A PROGRAM: by the building's purpose and the storey's role
        // (lower / middle / top — interior_archetypes.ts STOREY_PLANS), and a storey too small for it takes the
        // family's COMPACT program (`simpler`, a rung the ladder only had on the ground floor) before it falls further.
        const storey = ctx.storeys && f >= 1 ? storeyProgramFor(archetype, f, spec.floors, storeyStart) : null;
        const program = storey ? storey.program : programFor(archetype, f);
        if (!program) { floors.push(bareFloor(ctx, base, 'unprogrammed', 'empty', lift)); if (ctx.storeys && f >= 1) reasons.push(`f${f}:empty:no-storey-program`); continue; }
        let win = tryProgram(archetype, 'full', false, storey ? program : undefined), floorRung: PlanRung = 'full';
        const fb = archetype.fallback ? ARCHETYPES[archetype.fallback] : null;
        if (!win && fb && f === 0) { win = tryProgram(fb, 'simpler', false); floorRung = 'simpler'; }
        if (!win && storey) { win = tryProgram(archetype, 'simpler', false, storey.compact); floorRung = 'simpler'; }
        if (!win) { win = tryProgram(f === 0 && fb ? fb : archetype, 'sparse', true, storey ? program : undefined); floorRung = 'sparse'; }
        if (!win && storey) { win = tryProgram(archetype, 'sparse', true, storey.compact); floorRung = 'sparse'; }
        if (win) {
          if (ctx.rooms) win.res = enclose(ctx, win); // (slice G) the rooms, before the room is dressed round them
          // (slice U, the storeys revision) A DOORWAY OFF A SLOT IS A REASON TO LOOK AGAIN: where the chosen
          // arrangement's rooms leave a doorway with no open floor before it (two rooms 0.9 m apart, the door between
          // them), the other valid arrangements of the same program are enclosed too, best first, and the first whose
          // doorways all open onto open floor is the room. None → the chosen one stands (and says so).
          if (ctx.storeys && slotDoors(win) > 0) {
            const others = alternates.filter((v) => v !== win && !v.report.id.endsWith(':units')).sort((p, q) => q.report.score - p.report.score);
            let better: Evaluated | null = null;
            for (const alt of others) { alt.res = enclose(ctx, alt); if (slotDoors(alt) === 0) { better = alt; break; } }
            if (better) win = better; else win.c.reasons.push('room:door-off-slot');
          }
          const res = dress(ctx, win, storey ? storey.compact : null);
          floors.push(buildFloor(ctx, win.c, res, lift));
          for (const r of win.c.reasons) reasons.push(`f${f}:${r}`);
          if (f === 0) { rung = floorRung; chosen = win.report.id; status = floorRung === 'full' ? 'planned' : 'fallback'; }
          else if (floorRung !== 'full') reasons.push(`f${f}:rung:${floorRung}`);
          continue;
        }
        // the baseline retained: today's plan for this floor
        const lf = f < LEGACY_FLOORS ? legacyFloor(ctx, base, legacyPurpose, lift, hard) : bareFloor(ctx, base, 'unprogrammed', 'empty', lift);
        if (lf.pieces.filter((p) => p.tier !== 'owned').length === 0) { lf.source = 'empty'; reasons.push(`f${f}:empty:nothing-fits`); }
        else reasons.push(`f${f}:rung:legacy`);
        floors.push(lf);
        if (f === 0) {
          rung = lf.source === 'empty' ? 'empty' : 'legacy'; status = lf.source === 'empty' ? 'empty' : 'fallback'; chosen = `f0:${rung}`;
        }
      }
    }
  }

  // Vertical links: every stair run, and the lift candidate's rides.
  const links: FloorLink[] = [];
  const st = spec.stair;
  for (let f = 0; f + 1 < spec.floors; f++) {
    const foot = floors[f].routeNodes.some((n) => n.id === 'stair-foot'), land = floors[f + 1].routeNodes.some((n) => n.id === 'landing');
    if (foot && land) links.push({ kind: 'stair', from: { floor: f, node: 'stair-foot' }, to: { floor: f + 1, node: 'landing' }, costM: Math.round((Math.hypot(st.d, FLOOR_H) + 1.3) * 100) / 100, candidate: false });
  }
  let liftCandidate: LiftCandidate | null = null;
  if (lift) {
    const shaft: Bx = { x0: Math.min(lift.box.x0, lift.apron.x0), x1: Math.max(lift.box.x1, lift.apron.x1), z0: Math.min(lift.box.z0, lift.apron.z0), z1: Math.max(lift.box.z1, lift.apron.z1) };
    const calls = floors.map((fl) => ({
      floor: fl.floor, x: lift.call.x / 1000, z: lift.call.z / 1000, facing: lift.call.facing, anchor: 'lift/call/lift-call#0', reachable: fl.routeNodes.some((n) => n.id === 'lift-call'),
      // a LEGACY floor is today's plan untouched and an OWNED piece is its owner's: either may stand in the shaft — said here, never moved
      clear: fl.pieces.every((p) => { if (!p.blocksWalk) return true; const h = boundHalf(p.key, p.yaw); const hx = Math.ceil(h.hx * 1000 - 1e-6), hz = Math.ceil(h.hz * 1000 - 1e-6); return !meets({ x0: mmOf(p.x) - hx, x1: mmOf(p.x) + hx, z0: mmOf(p.z) - hz, z1: mmOf(p.z) + hz }, shaft); }),
    }));
    liftCandidate = { rect: rectOf(lift.box), wall: lift.wall, door: lift.door, calls };
    for (let f = 0; f + 1 < spec.floors; f++) if (calls[f].reachable && calls[f + 1].reachable) links.push({ kind: 'lift', from: { floor: f, node: 'lift-call' }, to: { floor: f + 1, node: 'lift-call' }, costM: FLOOR_H, candidate: true });
  } else if (spec.floors >= 3) reasons.push('lift:no-room');

  const recipe: InteriorRecipe = {
    schemaVersion: DESCRIPTOR_SCHEMA, layoutRevision, algorithmVersion: ALGORITHM_VERSION, algorithm: legacyAsked ? 'legacy' : 'planner',
    archetypeId: archetype.id, archetypeRevision: archetype.revision, shellSignature: shellSignature(input, spec), kitVersion: KIT_VERSION,
    catalogSignature: catalogSignature(), seedNamespace: seedNs, purpose: input.purpose, tradition, stairZone, deed,
    policy: { K: ctx.K, R: ctx.R, cellMm: CELL_MM }, ownedRevision: input.ownedRevision ?? ((input.owned?.length ?? 0) > 0 ? hash128Hex((input.owned ?? []).map((o) => `${o.id}:${o.key}:${o.floor}:${mmOf(o.x)}:${mmOf(o.z)}`).join('|')).slice(0, 12) : ''),
  };
  const body = { buildingId: input.buildingId, recipe, status, rung, reasons, floors, links, liftCandidate };
  const descriptor: InteriorDescriptor = { ...body, digest: descriptorDigest(body) };
  if (!legacyAsked) hard.push(...validateDescriptor(descriptor, input));
  return { descriptor, report: { candidates, chosen, reserved, work, hard } };
}

// ─── The hard validation of a finished descriptor (an INDEPENDENT pass over what is returned) ───────────────────────

/** Re-check a descriptor against the hard constraints from scratch: the legacy functions on every generated piece, real
 *  box overlap, the shell's solids, the console's ground, the lift, headroom, a fresh capsule route to every anchor and
 *  node. Returns the violations (empty = clean). Slice C may call it on a descriptor it is about to render. */
export function validateDescriptor(d: InteriorDescriptor, input: PlanInput): string[] {
  syncCatalog();
  const out: string[] = [];
  const liftRule = liftRuleOf(input.key, d.recipe.layoutRevision);
  const spec = interiorSpec(input.w, input.h, input.d, input.key, { liftRule });
  const work: PlanReport['work'] = { candidates: 0, repairs: 0, bfs: 0, cells: 0, placements: 0, tries: 0 };
  const lawFrame: PlanFrame = { hw: spec.hw, hd: spec.hd, stair: spec.stair };
  const lift = liftCandidateFor(spec, d.recipe.deed, lawFrame, liftRule);
  for (const fl of d.floors) {
    if (fl.source !== 'planned') continue;
    const base = buildBase(spec, fl.floor, input, d.recipe.stairZone, lift, d.recipe.layoutRevision >= STOREYS_LAYOUT_REVISION && d.recipe.algorithm !== 'legacy');
    const gen = fl.pieces.filter((p) => p.tier !== 'owned');
    const boxes = new Map<DescPiece, Bx>();
    for (const p of fl.pieces) { const h = boundHalf(p.key, p.yaw); const hx = Math.ceil(h.hx * 1000 - 1e-6), hz = Math.ceil(h.hz * 1000 - 1e-6); boxes.set(p, { x0: mmOf(p.x) - hx, x1: mmOf(p.x) + hx, z0: mmOf(p.z) - hz, z1: mmOf(p.z) + hz }); }
    for (const p of gen) {
      const bx = boxes.get(p)!;
      const prop = isKitProp(p.key);
      if (!FURNITURE[p.key] && !(prop && pieceMeta(p.key))) { out.push(`f${fl.floor}:unknown-key:${p.slot}`); continue; }
      if (!slotClear(base.frame, p.x, p.z)) out.push(`f${fl.floor}:centre-rule:${p.slot}`);
      if (!(prop ? pieceFootprintClear(base.frame, p.key, p.x, p.z, p.yaw) : footprintClear(base.frame, p.key, p.x, p.z, p.yaw))) out.push(`f${fl.floor}:footprint:${p.slot}`);
      if (p.on) continue;
      for (const s of base.solids) if (meets(bx, s)) { out.push(`f${fl.floor}:in-solid:${p.slot}`); break; }
      for (const s of base.walked) if (meets(bx, s)) { out.push(`f${fl.floor}:on-dais:${p.slot}`); break; }
      if ((pieceMeta(p.key)?.drawn.h ?? 0) * 1000 > base.headroom - 50) out.push(`f${fl.floor}:headroom:${p.slot}`);
      if (!p.blocksWalk) continue;
      for (const k of base.keep) if (meets(bx, k)) { out.push(`f${fl.floor}:reserved-ground:${p.slot}`); break; }
    }
    const blockers = fl.pieces.filter((p) => p.blocksWalk);
    for (let i = 0; i < blockers.length; i++) for (let j = i + 1; j < blockers.length; j++) {
      if (meets(boxes.get(blockers[i])!, boxes.get(blockers[j])!)) out.push(`f${fl.floor}:overlap:${blockers[i].slot}+${blockers[j].slot}`);
    }
    // dependencies and the priority order
    const order = new Map(fl.pieces.map((p) => [p.slot, p.order]));
    for (const p of gen) for (const n of [...(p.needs ?? []), ...(p.on ? [p.on] : [])]) if (!(order.has(n) && order.get(n)! < p.order)) out.push(`f${fl.floor}:order:${p.slot}<-${n}`);
    // (slice G) the partitions: nothing stands in one; the route is proven with every wall solid
    const wallBxs: Bx[] = fl.partitions.flatMap((w) => partitionBoxes(w).map((q) => ({ x0: mmOf(q.x0), x1: mmOf(q.x1), z0: mmOf(q.z0), z1: mmOf(q.z1) })));
    if (wallBxs.length > 0) for (const p of gen) { if (p.on) continue; const bx = boxes.get(p)!; for (const w of wallBxs) if (meets(bx, w)) { out.push(`f${fl.floor}:in-partition:${p.slot}`); break; } }
    // the route, from scratch
    const cells = new Uint8Array(base.raster0);
    for (const p of blockers) if (p.tier !== 'owned') Raster.block(cells, base, boxes.get(p)!, GROW);
    for (const w of wallBxs) Raster.block(cells, base, w, GROW);
    const seed = Raster.index(base, base.entry.x, base.entry.z);
    const f2 = flood(base, cells, seed >= 0 && !cells[seed] ? seed : nearestFree(base, cells, base.entry.x, base.entry.z, 1500), work);
    for (const a of fl.anchors) {
      const idx = Raster.index(base, mmOf(a.ax), mmOf(a.az));
      const okCell = idx >= 0 && !cells[idx] && f2.dist[idx] >= 0 ? idx : nearestFree(base, cells, mmOf(a.ax), mmOf(a.az), 250, f2.dist);
      if (okCell < 0) out.push(`f${fl.floor}:anchor-unreachable:${a.id}`);
      const env: Bx = { x0: mmOf(a.ax - a.envelope.hx), x1: mmOf(a.ax + a.envelope.hx), z0: mmOf(a.az - a.envelope.hz), z1: mmOf(a.az + a.envelope.hz) };
      if (a.kind === 'service-staff' || a.kind === 'service-customer' || a.kind === 'bedside' || a.kind === 'perform' || a.kind === 'queue') {
        for (const p of blockers) if (!a.seat && meets(env, boxes.get(p)!)) { out.push(`f${fl.floor}:envelope:${a.id}<-${p.slot}`); break; }
      }
    }
    for (const n of base.nodes) if (n.required && nearestFree(base, cells, n.x, n.z, 900, f2.dist) < 0) out.push(`f${fl.floor}:node-unreachable:${n.id}`);
    // (slice G) NOTHING IS SEALED: every room has ground the route reaches, every doorway is wide enough for the
    // capsule's raster and reached on both sides, every room is joined to the entry's room, a unit has ONE doorway
    if (fl.rooms.length > 1 || fl.doorways.length > 0) {
      const at = (x: number, z: number): boolean => nearestFree(base, cells, mmOf(x), mmOf(z), 350, f2.dist) >= 0;
      for (const r of fl.rooms) if (!at(r.x, r.z)) out.push(`f${fl.floor}:room-sealed:${r.id}`);
      for (const q of fl.doorways) {
        if (mmOf(q.width) - 2 * GROW < 2 * CELL_MM) out.push(`f${fl.floor}:doorway-narrow:${q.id}`);
        if (!at(q.ax, q.az) || !at(q.bx, q.bz)) out.push(`f${fl.floor}:doorway-blocked:${q.id}`);
      }
      const seen = new Set<string>([fl.rooms[0]?.id ?? '']);
      for (let grew = true; grew;) { grew = false; for (const q of fl.doorways) { if (seen.has(q.a) !== seen.has(q.b)) { seen.add(q.a); seen.add(q.b); grew = true; } } }
      for (const r of fl.rooms) if (!seen.has(r.id)) out.push(`f${fl.floor}:room-unlinked:${r.id}`);
      for (const r of fl.rooms) if (r.unitId) { const k = fl.doorways.filter((q) => q.a === r.id || q.b === r.id).length; if (k !== 1) out.push(`f${fl.floor}:unit-doorways:${r.unitId}:${k}`); }
    }
  }
  return out;
}

// ─── The pack view (what a furnish pack seats, and the storage ordinal of each piece) ──────────────────────────────────

export interface PackPiece {
  piece: DescPiece;
  /** The piece's position in the floor's priority order (= `piece.order`). */
  ordinal: number;
  /** Its rank among the floor's generated pieces OF THE SAME KEY, in priority order (the first chest is chest 0). */
  rank: number;
  /** The storage identity (the string `FurnitureUseModule` / the server key a container, a planter or a guest book by). */
  storageKey: string;
}
/**
 * THE PACK PREFIX LAW, as a function: per furnished floor, the first `packSlotCount(pack, n)` GENERATED pieces in
 * priority order (`n` = the floor's generated pieces), on the lowest `packFloors(pack, floors)` floors — the SAME two
 * functions today's `packPlan` uses. A legacy descriptor's view IS `packPlan(...)` slot for slot (the harness proves
 * it); a planned floor's order was built so that every prefix is a valid room (dependencies and supports inside it,
 * the essentials first).
 *
 * THE STORAGE IDENTITY (proposed for slice C; the server's key rule `pack:<floor>:<n>:<key>` is unchanged):
 *   · a LEGACY floor (layout 0): `n` = the slot's index in today's plan — byte for byte the key today's furnisher
 *     writes, so every existing container keeps its piece;
 *   · a PLANNED floor: `n` = the piece's RANK among the floor's pieces of the same key. The key string already names
 *     the piece's key, so (floor, rank, key) is unique; and a rank only moves when two pieces OF ONE KEY swap places —
 *     it survives a bigger pack (a prefix keeps earlier pieces earlier), the detail pass, and any kit revision that
 *     re-orders or adds pieces of OTHER keys. A raw ordinal survives only the first of those.
 */
export function packView(d: InteriorDescriptor, pack: FurnishPack): PackPiece[][] {
  const out: PackPiece[][] = [];
  const floors = [...d.floors].sort((a, b) => a.floor - b.floor);
  for (let f = 0; f < packFloors(pack, floors.length); f++) {
    const gen = floors[f].pieces.filter((p) => p.tier !== 'owned').sort((a, b) => a.order - b.order);
    const legacy = floors[f].source === 'legacy';
    const seen = new Map<string, number>();
    out.push(gen.slice(0, packSlotCount(pack, gen.length)).map((piece, ordinal) => {
      const rank = seen.get(piece.key) ?? 0;
      seen.set(piece.key, rank + 1);
      return { piece, ordinal, rank, storageKey: `pack:${piece.floor}:${legacy ? ordinal : rank}:${piece.key}` };
    }));
  }
  return out;
}

export type { Tradition };
